<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12">
        <v-btn text @click="backToGigs">
          <v-icon start>mdi-arrow-left</v-icon>
          Back to Gigs
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" closable @click:close="error = null">
      {{ error }}
    </v-alert>

    <v-row v-if="!gig && !error">
      <v-col cols="12">
        <v-skeleton-loader type="article" />
      </v-col>
    </v-row>

    <v-row v-else-if="gig">
      <!-- Gig Header -->
      <v-col cols="12">
        <v-card>
          <v-card-item>
            <template #title>{{ gig.name }}</template>
            <template #append>
              <v-btn v-if="orgStore.isManagerPlus" size="small" variant="tonal" class="mr-2" @click="showDuplicateDialog = true">
                <v-icon start size="small">mdi-content-copy</v-icon>
                Duplicate
              </v-btn>
              <v-btn v-if="orgStore.isManagerPlus" size="small" variant="tonal" @click="openEditGig">
                <v-icon start size="small">mdi-pencil</v-icon>
                Edit
              </v-btn>
            </template>
          </v-card-item>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="4">
                <p class="text-caption text-medium-emphasis">Client</p>
                <p class="text-body2">{{ clientName(gig.clientId) }}</p>
              </v-col>
              <v-col cols="12" sm="4">
                <p class="text-caption text-medium-emphasis">Date</p>
                <p class="text-body2">{{ gigDateDisplay }}</p>
                <p class="text-caption text-medium-emphasis">{{ gigZone }}</p>
              </v-col>
              <v-col v-if="gig.venueId" cols="12" sm="4">
                <p class="text-caption text-medium-emphasis">Venue</p>
                <p class="text-body2">{{ venueLabel(gig.venueId) }}</p>
              </v-col>
              <v-col v-if="orgStore.isManagerPlus" cols="12" sm="4">
                <p class="text-caption text-medium-emphasis">Quote total</p>
                <p class="text-body2">{{ quote ? '$' + quote.total.toLocaleString() : '—' }}</p>
              </v-col>
              <v-col v-if="gig.description" cols="12">
                <p class="text-caption text-medium-emphasis">Description</p>
                <p class="text-body2">{{ gig.description }}</p>
              </v-col>
              <v-col cols="12" sm="4">
                <p class="text-caption text-medium-emphasis">State</p>
                <v-select
                  v-if="orgStore.isManagerPlus"
                  v-model="gig.state"
                  :items="availableStates"
                  density="compact"
                  hide-details
                  @update:model-value="updateGigState"
                ></v-select>
                <p v-else class="text-body2">{{ gig.state }}</p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Calls Section — call CRUD only; positions/assignment/offers live
           in Staffing Budget below. -->
      <v-col cols="12" class="mt-6">
        <v-row class="mb-4">
          <v-col cols="12" sm="6">
            <h2>Calls</h2>
          </v-col>
          <v-col v-if="orgStore.isManagerPlus" cols="12" sm="6" class="text-right">
            <v-btn
              v-if="calls.length === 0"
              size="small"
              color="primary"
              class="mr-2"
              @click="openStandardDialog"
            >
              <v-icon start>mdi-calendar-multiple</v-icon>
              Set Up Calls
            </v-btn>
            <v-btn size="small" @click="openAddCall">
              <v-icon start>mdi-plus</v-icon>
              Add Call
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="calls.length === 0">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-8">
                <p class="text-body2 text-medium-emphasis">
                  No calls yet — "Set Up Calls" creates show, setup and strike together.
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col v-for="call in calls" :key="call.id" cols="12">
            <v-card class="mb-4">
              <v-card-item>
                <template #title>
                  <v-chip size="small" class="mr-2">{{ call.type }}</v-chip>
                  {{ formatCallSpan(call) }}
                </template>
                <template v-if="orgStore.isManagerPlus" #append>
                  <v-btn icon="mdi-pencil" size="small" variant="text" color="primary" @click="openEditCall(call)" />
                  <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="deleteCall(call.id)" />
                </template>
              </v-card-item>

              <v-card-text>
                <p v-if="call.venueId" class="text-body2 mb-2">
                  <strong>Venue:</strong> {{ venueLabel(call.venueId) }}
                </p>
                <p v-if="call.notes" class="text-body2">
                  <strong>Notes:</strong> {{ call.notes }}
                </p>

                <v-row class="mt-4 mb-2">
                  <v-col cols="12">
                    <h4>Positions covering this call</h4>
                  </v-col>
                </v-row>

                <div v-if="positionsForCall(call.id).length">
                  <div v-for="pos in positionsForCall(call.id)" :key="pos.id" class="mb-3">
                    <v-chip size="small" class="mb-1">
                      {{ pos.role }}<span v-if="pos.label"> ({{ pos.label }})</span> — {{ pos.state }}
                    </v-chip>
                    <div v-if="bookedPeopleForCall(call.id, pos.id).length" class="pl-2">
                      <div v-for="entry in bookedPeopleForCall(call.id, pos.id)" :key="entry.person.id" class="text-body2 mb-1">
                        {{ entry.person.name }}
                        <span v-if="entry.booking.state === 'OFFERED'" class="text-medium-emphasis">(Offered)</span>
                        <span v-if="entry.person.email || entry.person.phone" class="text-medium-emphasis">
                          — <span v-if="entry.person.email">{{ entry.person.email }}</span><span v-if="entry.person.email && entry.person.phone">, </span><span v-if="entry.person.phone">{{ entry.person.phone }}</span>
                        </span>
                        <template v-if="entry.booking.callIds.length === 0">
                          <br />
                          <span class="text-caption text-warning">
                            Held for the whole day — not yet scheduled for this specific call
                          </span>
                          <v-btn
                            v-if="!orgStore.isStaff"
                            size="x-small"
                            variant="text"
                            color="primary"
                            class="ml-1"
                            @click="addBookingToCall(entry, pos, call.id)"
                          >
                            Add to this call
                          </v-btn>
                        </template>
                      </div>
                    </div>
                    <p v-else class="text-body2 text-medium-emphasis pl-2">Not yet booked</p>
                  </div>
                </div>
                <p v-else class="text-body2 text-medium-emphasis">
                  No positions linked yet — add one from Staffing Budget above.
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- Staffing Budget Section — pricing detail, not for staff. Primary
           place to budget, link calls, assign, and send offers — see the
           "Calls" section below for call CRUD only. -->
      <v-col v-if="!orgStore.isStaff" cols="12" class="mt-6">
        <v-card>
          <v-card-item>
            <template #title>Staffing Budget</template>
            <template #append>
              <span
                class="text-body2 mr-4"
                :class="budgetTotals.budgeted > 0 && budgetTotals.assigned > budgetTotals.budgeted ? 'text-error font-weight-medium' : 'text-medium-emphasis'"
              >
                ${{ budgetTotals.assigned.toLocaleString() }} offered of ${{ budgetTotals.budgeted.toLocaleString() }} budgeted
              </span>
              <span
                v-if="hasActuals"
                class="text-body2 mr-4"
                :class="actuals.totals.overBudget ? 'text-error font-weight-medium' : 'text-medium-emphasis'"
                data-testid="actual-total"
              >
                ${{ actuals.totals.approvedCost.toLocaleString() }} actual<template v-if="actuals.totals.projectedCost > actuals.totals.approvedCost">
                  (+${{ (actuals.totals.projectedCost - actuals.totals.approvedCost).toLocaleString() }} pending)</template>
              </span>
              <v-btn size="small" @click="openAddPosition">
                <v-icon start size="small">mdi-plus</v-icon>
                Add Position
              </v-btn>
            </template>
          </v-card-item>
          <v-card-text class="pt-0">
            <v-alert v-if="costZoneInfo.source === 'default'" type="warning" variant="tonal" density="compact" class="mb-3" data-testid="cost-zone-warning">
              No time zone is set for this gig's venue or the organization, so costs are grouping days by UTC. A call after
              about 7 PM Central can land on the next day and be billed as a second day rate. Set the venue's time zone
              (Venues) or the organization's (Settings).
            </v-alert>
            <SharedDayPanel
              v-if="gig"
              :days="gigDays"
              :gig-id="gig.id"
              :gig-names="gigNames"
              :member-data="sharedMemberData"
              :estimate="sharedCosts?.estimate"
              :can-manage="orgStore.isCoordinatorPlus"
              :busy="sharedStore.busy"
              @send="sendSharedDayOffer"
              @unlink="unlinkSharedDayAction"
            />
            <p v-if="!gigsStore.positions.length" class="text-body2 text-medium-emphasis py-6 text-center">
              No positions budgeted yet.
            </p>
            <v-card v-for="pos in gigsStore.positions" :key="pos.id" variant="outlined" class="mb-3">
              <v-card-text class="py-3">
                <div class="d-flex align-center flex-wrap">
                  <div class="flex-grow-1">
                    <div class="font-weight-medium">
                      {{ pos.role }}<span v-if="pos.label" class="text-medium-emphasis"> — {{ pos.label }}</span>
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      Qty: {{ pos.quantity }} × {{ rateLabel(pos.rate, pos.rateType) }}
                      — Budget ${{ (pos.maxBudget ?? 0).toLocaleString() }}
                      — Offered
                      <span :class="isOverBudget(pos.id) ? 'text-error font-weight-medium' : ''">
                        ${{ (budgetGroupForPosition(pos.id)?.actualOfferedCost ?? 0).toLocaleString() }}
                      </span>
                      <template v-if="actualsForPosition(pos.id) && (actualsForPosition(pos.id)!.projectedCost > 0)">
                        — Actual
                        <span :class="actualsForPosition(pos.id)!.overBudget ? 'text-error font-weight-medium' : ''">
                          ${{ actualsForPosition(pos.id)!.approvedCost.toLocaleString() }}
                        </span>
                        <span v-if="actualsForPosition(pos.id)!.projectedCost > actualsForPosition(pos.id)!.approvedCost" class="text-warning">
                          (+${{ (actualsForPosition(pos.id)!.projectedCost - actualsForPosition(pos.id)!.approvedCost).toLocaleString() }} pending)
                        </span>
                      </template>
                      <span v-if="(budgetGroupForPosition(pos.id)?.cancellationFeeTotal ?? 0) > 0" class="text-warning">
                        — Fees ${{ (budgetGroupForPosition(pos.id)?.cancellationFeeTotal ?? 0).toLocaleString() }}
                      </span>
                    </div>
                    <div class="mt-1">
                      <v-chip v-for="c in positionCalls(pos)" :key="c.id" size="x-small" class="mr-1 mb-1">
                        {{ c.type }} {{ formatCallSpan(c) }}
                      </v-chip>
                      <span v-if="!positionCalls(pos).length" class="text-caption text-medium-emphasis">
                        Unscheduled<template v-if="pos.venueId"> — {{ venueLabel(pos.venueId) }}</template><template v-if="pos.scopeDate"> — {{ formatDate(pos.scopeDate, gigZone) }}</template>
                      </span>
                    </div>
                  </div>
                  <div class="text-right">
                    <v-btn size="small" variant="tonal" class="mr-1 mb-1" @click="openOfferDialog(pos)">
                      <v-icon start size="small">mdi-account-plus</v-icon>
                      Assign
                    </v-btn>
                    <v-btn size="small" variant="text" class="mr-1 mb-1" @click="openManageCalls(pos)">
                      <v-icon start size="small">mdi-calendar-edit</v-icon>
                      Calls
                    </v-btn>
                    <v-btn icon="mdi-pencil" size="small" variant="text" color="primary" @click="openEditPosition(pos)" />
                    <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="deletePosition(pos)" />
                  </div>
                </div>

                <template v-if="sortedPositionBookings(pos.id).length">
                  <v-divider class="my-2" />
                  <v-list density="compact">
                    <v-list-item
                      v-for="b in sortedPositionBookings(pos.id)"
                      :key="b.id"
                      :class="TERMINAL_INACTIVE_STATES.has(b.state) ? 'text-medium-emphasis' : ''"
                    >
                      <v-list-item-title>
                        {{ personDisplayName(b) }}
                        <v-chip v-if="b.sharedDayId" size="x-small" color="primary" variant="tonal" class="ml-1" data-testid="shared-chip">
                          <v-icon start size="x-small">mdi-link-variant</v-icon>Shared day
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <template v-if="b.state === 'CANCELLED'">
                          Cancelled {{ b.cancelledAt ? formatDate(b.cancelledAt) : '' }}
                          <template v-if="b.cancellationFee">
                            — Fee ${{ b.cancellationFee.amount.toLocaleString() }} ({{ b.cancellationFeeStatus?.toLowerCase() }})
                          </template>
                          <template v-if="b.cancellationReason"> — {{ b.cancellationReason }}</template>
                        </template>
                        <template v-else>
                          <div>
                            <template v-if="b.rate">{{ rateLabel(b.rate, b.rateType) }}</template>
                            <template v-if="personContact(b)"> — {{ personContact(b) }}</template>
                          </div>
                          <div v-if="bookingCalls(b).length">
                            <div v-for="c in bookingCalls(b)" :key="c.id">{{ c.type }} — {{ formatCallSpan(c) }}</div>
                          </div>
                          <div v-else>Unscheduled (day/venue only)</div>
                        </template>
                      </v-list-item-subtitle>
                      <template #append>
                        <v-btn
                          v-if="!TERMINAL_INACTIVE_STATES.has(b.state)"
                          icon="mdi-calendar-edit"
                          size="x-small"
                          variant="text"
                          class="mr-1"
                          title="Edit which calls this person covers"
                          @click="openBookingCalls(pos, b)"
                        />
                        <v-chip size="x-small" :color="bookingStateColor(b.state)" class="mr-2">
                          {{ b.state }}
                        </v-chip>
                        <template v-if="b.state === 'DRAFT'">
                          <v-btn
                            icon="mdi-currency-usd"
                            size="x-small"
                            variant="text"
                            class="mr-1"
                            title="Edit this person's rate"
                            @click="openEditBookingRate(pos, b)"
                          />
                          <v-btn
                            v-if="!b.sharedDayId && b.callIds.length && orgStore.isCoordinatorPlus"
                            icon="mdi-link-variant"
                            size="x-small"
                            variant="text"
                            class="mr-1"
                            title="Link with this person's other booking the same day"
                            data-testid="link-shared-day"
                            @click="openLinkSharedDay(pos, b)"
                          />
                          <v-btn
                            size="x-small"
                            color="primary"
                            variant="text"
                            :loading="sendingOfferId === b.id"
                            @click="openOfferPreview(b)"
                          >
                            Send Offer
                          </v-btn>
                          <v-btn
                            icon="mdi-close"
                            size="x-small"
                            variant="text"
                            color="error"
                            :loading="unassigningId === b.id"
                            @click="unassignAction(b)"
                          />
                        </template>
                        <template v-if="b.state === 'OFFERED'">
                          <v-btn
                            size="x-small"
                            color="success"
                            variant="text"
                            :loading="respondingId === b.id"
                            @click="respondBooking(b, true)"
                          >
                            Accept
                          </v-btn>
                          <v-btn
                            size="x-small"
                            color="error"
                            variant="text"
                            :loading="respondingId === b.id"
                            @click="respondBooking(b, false)"
                          >
                            Decline
                          </v-btn>
                          <v-btn
                            size="x-small"
                            variant="text"
                            :loading="cancellingId === b.id"
                            @click="openCancelBooking(pos, b)"
                          >
                            Pull Offer
                          </v-btn>
                        </template>
                        <template v-if="b.state === 'ACCEPTED' || b.state === 'BOOKED'">
                          <v-btn
                            size="x-small"
                            color="error"
                            variant="text"
                            :loading="cancellingId === b.id"
                            @click="openCancelBooking(pos, b)"
                          >
                            Cancel
                          </v-btn>
                        </template>
                      </template>
                    </v-list-item>
                  </v-list>
                </template>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Labor overage — netted across positions; manager+ decides bill vs
           absorb. Hidden until there's approved time or a prior decision. -->
      <v-col v-if="orgStore.isManagerPlus && hasCommittedBookings" cols="12" class="mt-6">
        <OverageCard
          ref="overageCardRef"
          :org-id="orgId()"
          :gig-id="gig.id"
          :actuals="actuals"
          :margin-pct="effectiveMarginPct"
        />
      </v-col>

      <!-- Timesheets — actual clock-in/out time; coordinator+ review it (staff
           can't read other people's entries, so it's not mounted for them). -->
      <v-col v-if="!orgStore.isStaff && hasCommittedBookings" cols="12" class="mt-6">
        <GigTimesheet
          :org-id="orgId()"
          :gig="gig"
          :calls="calls"
          :positions="gigsStore.positions"
          :bookings="allBookings"
          :actuals="actuals"
        />
      </v-col>

      <!-- Payroll — a saved snapshot of what the crew is owed; manager+ only
           (coordinators can't see crew pay totals). -->
      <v-col v-if="canManagePayroll" cols="12" class="mt-6">
        <PayrollCard :org-id="orgId()" :gig="gig" :actuals="actuals" />
      </v-col>

      <!-- Quote Section — pricing detail, manager+ only -->
      <v-col v-if="orgStore.isManagerPlus" cols="12" class="mt-6">
        <v-row class="mb-4">
          <v-col cols="12" sm="6">
            <h2>Quote</h2>
            <p class="text-caption text-medium-emphasis">Client: {{ clientName(gig.clientId) }}</p>
          </v-col>
          <v-col cols="12" sm="6" class="text-right">
            <v-text-field
              v-if="quoteEditable"
              v-model.number="marginPctInput"
              label="Margin %"
              type="number"
              density="compact"
              hide-details
              style="max-width: 100px; display: inline-block"
              class="mr-2"
              :placeholder="String(orgStore.currentOrg?.defaultLaborMarginPct ?? 0)"
              @change="saveMarginPct"
            />
            <v-btn
              v-if="quoteEditable"
              size="small"
              variant="tonal"
              class="mr-2"
              :loading="quotesStore.isSaving"
              @click="recomputeQuoteLabor"
            >
              <v-icon start size="small">mdi-refresh</v-icon>
              Recompute from Budget
            </v-btn>
            <v-btn
              v-if="quote && quote.state === 'DRAFT'"
              size="small"
              color="primary"
              :loading="quotesStore.isSaving"
              @click="handleSendQuote"
            >
              Send
            </v-btn>
            <template v-else-if="quote && quote.state === 'SENT'">
              <v-btn
                size="small"
                color="success"
                class="mr-2"
                @click="handleMarkApproved"
              >
                Mark Approved
              </v-btn>
              <v-btn size="small" variant="outlined" color="error" @click="handleMarkDeclined">
                Decline
              </v-btn>
            </template>
            <template v-else-if="quote && quote.state === 'APPROVED'">
              <v-chip size="small" color="success" class="mr-2">APPROVED</v-chip>
              <v-btn
                size="small"
                variant="tonal"
                class="mr-2"
                :loading="quotesStore.isSaving"
                @click="handleStartChangeOrder"
              >
                Create Change Order
              </v-btn>
              <v-btn
                v-if="!quote.invoiceNinjaInvoiceId"
                size="small"
                color="primary"
                :disabled="!canCreateInvoice"
                :loading="quotesStore.isInvoicing"
                @click="handleCreateInvoice"
              >
                Create Invoice
              </v-btn>
            </template>
            <v-chip v-else-if="quote" size="small" class="ml-2">{{ quote.state }}</v-chip>
          </v-col>
          <v-col
            v-if="quote?.state === 'APPROVED' && !quote.invoiceNinjaInvoiceId && !canCreateInvoice"
            cols="12"
            class="text-right"
          >
            <p class="text-caption text-medium-emphasis">
              {{ createInvoiceHint }}
            </p>
          </v-col>
        </v-row>

        <v-alert
          v-if="quote?.parentQuoteId"
          type="warning"
          variant="tonal"
          class="mb-4"
        >
          <strong>Change Order #{{ quote.changeOrderNumber }}</strong>
          <span v-if="parentQuote"> — supersedes the quote approved {{ formatDate(parentQuote.approvedAt) }}</span>.
          <div class="mt-1 text-body2">
            Previously approved: ${{ parentQuote?.total ?? '—' }} ·
            This change order: {{ changeOrderDelta >= 0 ? '+' : '' }}${{ changeOrderDelta }} ·
            New total: ${{ quote.total }}
          </div>
        </v-alert>

        <v-alert
          v-if="quote?.invoiceNinjaInvoiceId"
          type="info"
          variant="tonal"
          class="mb-4"
        >
          <div class="d-flex align-center justify-space-between flex-wrap">
            <span>
              Invoiced in Invoice Ninja —
              <a :href="invoiceUrl" target="_blank" rel="noopener">view invoice</a>
            </span>
            <v-btn
              size="small"
              variant="tonal"
              :loading="quotesStore.isRefreshingStatus"
              @click="handleRefreshStatus"
            >
              Refresh Payment Status
            </v-btn>
          </div>
          <p v-if="paymentStatusMsg" class="text-caption mt-2 mb-0">{{ paymentStatusMsg }}</p>
        </v-alert>

        <v-alert
          v-if="quotesStore.error"
          type="error"
          closable
          class="mb-4"
          @click:close="quotesStore.error = null"
        >
          {{ quotesStore.error }}
        </v-alert>

        <v-card v-if="!quote">
          <v-card-text class="text-center py-8">
            <p class="text-body2 text-medium-emphasis">
              No quote yet — recompute labor from the calls or add a line to start one.
            </p>
          </v-card-text>
        </v-card>

        <template v-else>
          <v-card class="mb-4">
            <v-card-item>
              <template #title>Labor</template>
            </v-card-item>
            <v-card-text>
              <v-table v-if="laborLines.length" density="compact">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th class="text-right">Actual Hrs</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Unit</th>
                    <th class="text-right">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in laborLines" :key="line.id">
                    <td>
                      <v-icon v-if="line.locked" size="x-small" class="mr-1">mdi-lock</v-icon>
                      {{ line.description }}
                    </td>
                    <td class="text-right">{{ line.hours ?? '—' }}</td>
                    <td class="text-right">{{ line.quantity }}</td>
                    <td class="text-right">${{ line.unitCost }}</td>
                    <td class="text-right">${{ line.lineTotal }}</td>
                    <td class="text-right">
                      <template v-if="quoteEditable">
                        <v-btn icon="mdi-pencil" size="x-small" variant="text" color="primary" @click="openEditLine(line)" />
                        <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click="removeQuoteLine(line.id)" />
                      </template>
                    </td>
                  </tr>
                </tbody>
              </v-table>
              <p v-else class="text-body2 text-medium-emphasis">
                No labor lines yet — click "Recompute from Budget".
              </p>
            </v-card-text>
          </v-card>

          <v-card class="mb-4">
            <v-card-item>
              <template #title>Items</template>
              <template v-if="quoteEditable" #append>
                <v-btn size="small" variant="tonal" class="mr-2" @click="openAddItemLine">
                  <v-icon start size="small">mdi-plus</v-icon>
                  Add Item
                </v-btn>
                <v-btn size="small" variant="tonal" @click="openAddManualLine">
                  <v-icon start size="small">mdi-plus</v-icon>
                  Add Manual Line
                </v-btn>
              </template>
            </v-card-item>
            <v-card-text>
              <v-table v-if="itemLines.length" density="compact">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Unit</th>
                    <th class="text-right">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in itemLines" :key="line.id">
                    <td>
                      <v-icon v-if="line.locked" size="x-small" class="mr-1">mdi-lock</v-icon>
                      {{ line.description }}
                      <div v-if="line.itemDescription" class="text-caption text-medium-emphasis">
                        {{ line.itemDescription }}
                      </div>
                    </td>
                    <td class="text-right">{{ line.quantity }}</td>
                    <td class="text-right">${{ line.unitCost }}</td>
                    <td class="text-right">${{ line.lineTotal }}</td>
                    <td class="text-right">
                      <template v-if="quoteEditable">
                        <v-btn icon="mdi-pencil" size="x-small" variant="text" color="primary" @click="openEditLine(line)" />
                        <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click="removeQuoteLine(line.id)" />
                      </template>
                    </td>
                  </tr>
                </tbody>
              </v-table>
              <p v-else class="text-body2 text-medium-emphasis">No item or manual lines yet.</p>
            </v-card-text>
          </v-card>

          <v-card>
            <v-card-text>
              <v-row dense>
                <v-col cols="6" sm="4">
                  <v-text-field
                    v-model.number="taxRateInput"
                    label="Tax %"
                    type="number"
                    density="compact"
                    hide-details
                    :disabled="!quoteEditable"
                    @change="saveTaxRate"
                  />
                </v-col>
                <v-col cols="6" sm="4">
                  <v-text-field
                    v-model.number="depositInput"
                    label="Deposit ($)"
                    type="number"
                    prefix="$"
                    density="compact"
                    hide-details
                    :disabled="!quoteEditable"
                    @change="saveDeposit"
                  />
                </v-col>
                <v-col cols="12" sm="4" class="text-right">
                  <p class="text-body2">Subtotal: ${{ quote.subtotal }}</p>
                  <p class="text-body2">Tax: ${{ quote.tax }}</p>
                  <p class="text-h6">Total: ${{ quote.total }}</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>
      </v-col>

      <!-- Contract Section — a frozen, signable snapshot of the Quote + the
           org's standing terms, sent to the client for e-signature. Purely
           informational here: signing never advances Quote/Gig state on its
           own (see handleMarkApproved above, unchanged by this section). Manager+ only. -->
      <v-col v-if="orgStore.isManagerPlus" cols="12" class="mt-6">
        <v-row class="mb-4">
          <v-col cols="12" sm="6">
            <h2>Contract</h2>
          </v-col>
          <v-col cols="12" sm="6" class="text-right">
            <v-btn
              size="small"
              color="primary"
              :disabled="!quote || quote.lineItems.length === 0"
              :loading="contractsStore.isSending"
              @click="handleSendContract"
            >
              {{ contract && contract.state !== 'SIGNED' ? 'Resend for Signature' : 'Send for Signature' }}
            </v-btn>
          </v-col>
        </v-row>

        <v-alert
          v-if="contractsStore.error"
          type="error"
          closable
          class="mb-4"
          @click:close="contractsStore.error = null"
        >
          {{ contractsStore.error }}
        </v-alert>

        <v-card v-if="!contract">
          <v-card-text class="text-center py-8">
            <p class="text-body2 text-medium-emphasis">No contract sent yet.</p>
          </v-card-text>
        </v-card>
        <v-card v-else>
          <v-card-text>
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div>
                <v-chip size="small" :color="contract.state === 'SIGNED' ? 'success' : undefined">{{ contract.state }}</v-chip>
                <span v-if="contract.state === 'SIGNED'" class="text-body2 ml-2">
                  Signed by "{{ contract.signatureName }}" on {{ contract.signatureTimestamp ? formatDate(contract.signatureTimestamp) : '' }}
                </span>
                <span v-else class="text-body2 ml-2">
                  Sent {{ contract.sentAt ? formatDate(contract.sentAt) : '' }} to {{ contract.clientEmail }} — awaiting signature
                </span>
              </div>
              <v-btn
                v-if="contract.state === 'SIGNED'"
                size="small"
                variant="tonal"
                :loading="contractsStore.isDownloading"
                @click="handleDownloadContractPdf"
              >
                <v-icon start size="small">mdi-download</v-icon>
                Download PDF
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add Item Line Dialog -->
    <v-dialog v-model="showAddItemDialog" width="450">
      <v-card>
        <v-card-title>Add Item</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveAddItemLine">
            <v-select
              v-model="addItemForm.itemId"
              :items="itemsStore.items"
              item-title="name"
              item-value="id"
              label="Item"
              class="mb-3"
            />
            <v-text-field
              v-model.number="addItemForm.quantity"
              label="Quantity"
              type="number"
              class="mb-3"
            />
            <div class="text-right">
              <v-btn text @click="showAddItemDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :disabled="!addItemForm.itemId">Add</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add Manual Line Dialog -->
    <v-dialog v-model="showAddManualDialog" width="450">
      <v-card>
        <v-card-title>Add Manual Line</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveAddManualLine">
            <v-text-field v-model="manualForm.description" label="Description" required class="mb-3" />
            <v-text-field
              v-model.number="manualForm.quantity"
              label="Quantity"
              type="number"
              class="mb-3"
            />
            <v-text-field
              v-model.number="manualForm.unitCost"
              label="Unit Cost"
              type="number"
              prefix="$"
              class="mb-3"
            />
            <div class="text-right">
              <v-btn text @click="showAddManualDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit">Add</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Line Dialog -->
    <v-dialog v-model="showEditLineDialog" width="400">
      <v-card>
        <v-card-title>Edit Line</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveEditLine">
            <p class="text-body2 text-medium-emphasis mb-3">{{ editingLine?.description }}</p>
            <v-text-field
              v-model.number="editLineForm.quantity"
              label="Quantity"
              type="number"
              class="mb-3"
            />
            <v-text-field
              v-model.number="editLineForm.unitCost"
              label="Unit Cost"
              type="number"
              prefix="$"
              class="mb-3"
            />
            <div class="text-right">
              <v-btn text @click="showEditLineDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Gig Dialog -->
    <v-dialog v-model="showGigDialog" width="560">
      <v-card>
        <v-card-title>Edit Gig</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveGig">
            <v-text-field v-model="gigForm.name" label="Name" required class="mb-3" />
            <div class="d-flex align-center mb-3">
              <v-autocomplete
                v-model="gigForm.clientId"
                :items="clientsStore.clients"
                item-title="name"
                item-value="id"
                label="Client"
                :hint="clientsStore.clients.length ? '' : 'No clients yet — add one'"
                persistent-hint
                class="flex-grow-1"
                hide-details="auto"
              />
              <v-btn icon="mdi-plus" size="small" variant="text" class="ml-2" @click="openNewClient" />
            </div>
            <v-text-field v-model="gigForm.date" label="Date" type="date" class="mb-3" />
            <v-autocomplete
              v-model="gigForm.venueId"
              :items="venueItems"
              label="Main venue"
              required
              :rules="[v => !!v || 'A venue is required']"
              :hint="`Required. Pre-fills onto new calls/positions, still changeable per call. Times are shown in ${gigFormZone}.`"
              persistent-hint
              class="mb-3"
            />
            <v-textarea v-model="gigForm.description" label="Description" rows="2" class="mb-3" />
            <v-select v-model="gigForm.state" :items="availableStates" label="State" class="mb-3" />
            <div class="text-right">
              <v-btn text @click="showGigDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="savingGig">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Changing the gig date: offer to move the calls with it -->
    <v-dialog v-model="showRescheduleDialog" width="560" persistent>
      <v-card>
        <v-card-title>Move the calls to the new date?</v-card-title>
        <v-card-text>
          <p class="mb-3">
            You're moving this gig from <strong>{{ reschedule.fromLabel }}</strong> to
            <strong>{{ reschedule.toLabel }}</strong>
            ({{ Math.abs(reschedule.dayOffset) }} day{{ Math.abs(reschedule.dayOffset) === 1 ? '' : 's' }}
            {{ reschedule.dayOffset > 0 ? 'later' : 'earlier' }}).
          </p>
          <p class="mb-3">
            <strong>Move calls too</strong> shifts {{ reschedule.callCount }} call{{ reschedule.callCount === 1 ? '' : 's' }}<template v-if="reschedule.positionDays"> and {{ reschedule.positionDays }} unscheduled position day{{ reschedule.positionDays === 1 ? '' : 's' }}</template>
            by the same number of days, keeping each start/end time (in the venue's time zone).
            <strong>Change date only</strong> leaves every call where it is.
          </p>
          <v-alert v-if="reschedule.affectedPeople > 0" type="warning" variant="tonal" density="compact" class="mb-3">
            {{ reschedule.affectedPeople }} {{ reschedule.affectedPeople === 1 ? 'person has' : 'people have' }} an open offer or a
            confirmed booking. Moving the calls updates their offers, and confirmed people get an updated
            calendar invite by email.
          </v-alert>
          <v-alert v-if="reschedule.blockedReason" type="info" variant="tonal" density="compact" class="mb-3">
            {{ reschedule.blockedReason }}
          </v-alert>
          <div class="text-right mt-2">
            <v-btn text @click="showRescheduleDialog = false">Cancel</v-btn>
            <v-btn variant="tonal" :loading="savingGig" @click="saveGigWith(false)">Change date only</v-btn>
            <v-btn
              color="primary"
              class="ml-2"
              :loading="savingGig"
              :disabled="!!reschedule.blockedReason"
              @click="saveGigWith(true)"
            >
              Move calls too
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Quick-add client, opened from the Edit Gig dialog -->
    <v-dialog v-model="showNewClientDialog" width="450">
      <v-card>
        <v-card-title>New Client</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveNewClient">
            <v-text-field v-model="newClient.name" label="Name" required class="mb-3" />
            <v-text-field v-model="newClient.contactEmail" label="Email" type="email" class="mb-3" />
            <v-text-field v-model="newClient.contactPhone" label="Phone" class="mb-3" />
            <div class="text-right">
              <v-btn text @click="showNewClientDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="savingNewClient">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Set Up Calls Dialog -->
    <v-dialog v-model="showStandardDialog" width="640">
      <v-card>
        <v-card-title>Set Up Calls</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="createStandardCalls">
            <v-autocomplete
              v-model="stdForm.venueId"
              :items="venueItems"
              label="Venue (applies to all)"
              :hint="`Times are in ${stdZone}`"
              persistent-hint
              class="mb-4"
            />
            <template v-for="key in (['show', 'setup', 'strike'] as const)" :key="key">
              <p class="text-subtitle-2 text-capitalize mt-2">{{ key }}</p>
              <v-row dense class="mb-2">
                <v-col cols="6">
                  <v-text-field
                    v-model="stdForm[key].start"
                    label="Start"
                    type="datetime-local"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="stdForm[key].end"
                    label="End"
                    type="datetime-local"
                    density="compact"
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>
            <div class="text-right mt-2">
              <v-btn text @click="showStandardDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="savingStd">Create Calls</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add / Edit Call Dialog -->
    <v-dialog v-model="showCallDialog" width="600">
      <v-card>
        <v-card-title>{{ callEditingId ? 'Edit Call' : 'Add Call' }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveCall">
            <v-select
              v-model="callForm.type"
              :items="['setup', 'show', 'strike']"
              label="Call Type"
              required
              class="mb-4"
            ></v-select>
            <v-text-field
              v-model="callForm.startTime"
              label="Start Time"
              type="datetime-local"
              required
              :hint="`Venue time (${callFormZone})`"
              persistent-hint
              class="mb-4"
            ></v-text-field>
            <v-text-field
              v-model="callForm.endTime"
              label="End Time"
              type="datetime-local"
              required
              :rules="[
                v => !v || !callForm.startTime || fromZonedInput(v, callFormZone) > fromZonedInput(callForm.startTime, callFormZone) || 'End time must be after start time',
              ]"
              class="mb-4"
            ></v-text-field>
            <v-autocomplete
              v-model="callForm.venueId"
              :items="venueItems"
              label="Venue"
              hint="Defaults to the gig's main venue — pick another to override it for this call"
              persistent-hint
              class="mb-4"
            />
            <v-textarea v-model="callForm.notes" label="Notes" rows="2" class="mb-4" />
            <div class="text-right">
              <v-btn text @click="showCallDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="savingCall">
                {{ callEditingId ? 'Save' : 'Add Call' }}
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Position Dialog -->
    <v-dialog v-model="showPositionDialog" width="620">
      <v-card>
        <v-card-title>{{ editingPositionId ? 'Edit Position' : 'Add Position' }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="savePosition">
            <v-btn-toggle v-model="positionForm.rateSource" mandatory divided class="mb-4">
              <v-btn value="card" size="small" :disabled="rateCardsStore.rateCards.length === 0">
                From rate card
              </v-btn>
              <v-btn value="manual" size="small">Manual</v-btn>
            </v-btn-toggle>

            <template v-if="positionForm.rateSource === 'card'">
              <v-select
                v-model="positionForm.rateCardId"
                :items="rateCardsStore.rateCards"
                item-title="name"
                item-value="id"
                label="Rate Card"
                class="mb-4"
              />
              <v-select
                v-model="positionForm.rateCardRole"
                :items="cardRoleNames"
                label="Role"
                class="mb-4"
              />
            </template>
            <template v-else>
              <v-text-field v-model="positionForm.role" label="Role" required class="mb-4" />
            </template>

            <v-text-field
              v-model="positionForm.label"
              label="Label (optional)"
              hint="Differentiate multiple positions with the same role, e.g. FOH / Monitors"
              persistent-hint
              class="mb-4"
            />

            <v-btn-toggle v-model="positionForm.rateType" mandatory divided class="mb-4">
              <v-btn value="day" size="small">Day rate</v-btn>
              <v-btn value="hourly" size="small">Hourly</v-btn>
            </v-btn-toggle>

            <v-text-field
              v-model.number="positionForm.amount"
              :label="positionForm.rateType === 'hourly' ? 'Rate ($/hr)' : 'Rate ($/day)'"
              type="number"
              prefix="$"
              :readonly="positionForm.rateSource === 'card'"
              required
              class="mb-4"
            />

            <v-text-field
              v-model.number="positionForm.quantity"
              label="Quantity"
              type="number"
              min="1"
              required
              class="mb-4"
            />
            <v-text-field
              v-model.number="positionForm.maxBudget"
              label="Total Budget (optional)"
              hint="Whole position — all quantity and every linked call combined. Not per-day, not per-head."
              persistent-hint
              type="number"
              prefix="$"
              class="mb-2"
            />
            <div v-if="suggestedBudget != null && suggestedBudget !== positionForm.maxBudget" class="mb-4 text-body2">
              Suggested from the linked calls: <strong>${{ suggestedBudget.toLocaleString() }}</strong>
              <v-btn size="x-small" variant="tonal" class="ml-2" @click="positionForm.maxBudget = suggestedBudget">Use</v-btn>
              <div class="text-caption text-medium-emphasis">
                Hourly rate × the linked calls' hours (with overtime and minimums from the rate card) × quantity.
              </div>
            </div>
            <div v-else class="mb-4"></div>

            <v-select
              v-model="positionForm.callIds"
              :items="callSelectItems"
              label="Linked Calls (optional)"
              multiple
              chips
              closable-chips
              hint="Leave empty to budget this role before pinning down specific calls — link them later."
              persistent-hint
              class="mb-4"
            />
            <template v-if="positionForm.callIds.length === 0">
              <v-autocomplete v-model="positionForm.venueId" :items="venueItems" label="Venue (shown on an unscheduled offer)" class="mb-4" />
              <v-text-field
                v-model="positionForm.scopeDate"
                label="Day (required for an unscheduled offer)"
                type="date"
                required
                :rules="[v => !!v || 'A day is required when no calls are linked']"
                class="mb-4"
              />
            </template>

            <div class="text-right">
              <v-btn text @click="showPositionDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="savingPosition">
                {{ editingPositionId ? 'Save' : 'Add Position' }}
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Manage Linked Calls Dialog -->
    <v-dialog v-model="showManageCallsDialog" width="480">
      <v-card>
        <v-card-title>Linked Calls — {{ manageCallsPosition?.role }}</v-card-title>
        <v-card-text>
          <p v-if="!calls.length" class="text-body2 text-medium-emphasis">Add calls to this gig first.</p>
          <v-checkbox
            v-for="c in calls"
            :key="c.id"
            v-model="manageCallIds"
            :value="c.id"
            :label="`${c.type} — ${formatCallSpan(c)}`"
            density="compact"
            hide-details
            class="mb-1"
          />
          <div class="text-right mt-4">
            <v-btn text @click="showManageCallsDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="savingManageCalls" @click="saveManageCalls">Save</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Assign Position Dialog -->
    <v-dialog v-model="showOfferDialog" width="480">
      <v-card>
        <v-card-title>Assign {{ offerPosition?.role }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="submitOffer">
            <v-autocomplete
              v-model="offerPersonId"
              :items="peopleStore.people"
              item-title="name"
              item-value="id"
              label="Person"
              :hint="peopleStore.people.length ? '' : 'Add people on the People page first'"
              persistent-hint
              class="mb-4"
            ></v-autocomplete>
            <v-text-field
              v-model.number="assignRate"
              label="Rate for this person"
              type="number"
              prefix="$"
              :suffix="offerPosition?.rateType === 'hourly' ? '/hr' : '/day'"
              :hint="assignRateHint"
              persistent-hint
              class="mb-4"
            />
            <template v-if="offerPositionCalls.length">
              <p class="text-caption text-medium-emphasis mb-1">Which of this position's calls is this person covering?</p>
              <v-checkbox
                v-for="c in offerPositionCalls"
                :key="c.id"
                v-model="offerCallIds"
                :value="c.id"
                :label="`${c.type} — ${formatCallSpan(c)}`"
                density="compact"
                hide-details
                class="mb-1"
              />
            </template>
            <p v-else class="text-caption text-medium-emphasis mb-4">
              No calls linked to this position yet — this assigns just the day/venue/role; link calls later.
            </p>
            <p v-if="offerMissingDate" class="text-caption text-error mb-4">
              This is a call-less assignment but the position has no Day set — edit the position to add one before assigning.
            </p>
            <div class="text-right">
              <v-btn text @click="showOfferDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="isOffering" :disabled="!offerPersonId || offerMissingDate">
                Assign (Draft)
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit a booking's own linked calls (subset of the position's) -->
    <v-dialog v-model="showBookingCallsDialog" width="480">
      <v-card>
        <v-card-title>Calls for {{ bookingCallsBooking ? personDisplayName(bookingCallsBooking) : '' }}</v-card-title>
        <v-card-text>
          <template v-if="bookingCallsOptions.length">
            <v-checkbox
              v-for="c in bookingCallsOptions"
              :key="c.id"
              v-model="bookingCallIds"
              :value="c.id"
              :label="`${c.type} — ${formatCallSpan(c)}`"
              density="compact"
              hide-details
              class="mb-1"
            />
          </template>
          <p v-else class="text-body2 text-medium-emphasis">
            No calls linked to this position yet — use "Calls" on the position first.
          </p>
          <div class="text-right mt-4">
            <v-btn text @click="showBookingCallsDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="savingBookingCalls" @click="saveBookingCalls">Save</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit a draft booking's rate — only while it's still DRAFT; once an
         offer is sent the rate is what was actually offered. -->
    <v-dialog v-model="showEditRateDialog" width="400">
      <v-card>
        <v-card-title>Edit Rate for {{ bookingRateBooking ? personDisplayName(bookingRateBooking) : '' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="bookingRateAmount"
            label="Rate"
            type="number"
            prefix="$"
            :suffix="bookingRatePosition?.rateType === 'hourly' ? '/hr' : '/day'"
            class="mb-2"
          />
          <div class="text-right mt-2">
            <v-btn text @click="showEditRateDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="savingBookingRate" @click="saveBookingRate">Save</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Cancel an offered/accepted/booked person. Pulling a still-OFFERED
         offer has no calendar footprint to cancel (never accepted, so
         never invited) and no fee makes sense — the fee section only shows
         once they'd actually committed. -->
    <v-dialog v-model="showCancelBookingDialog" width="480">
      <v-card>
        <v-card-title>Cancel {{ cancelBookingTarget ? personDisplayName(cancelBookingTarget) : '' }}</v-card-title>
        <v-card-text>
          <p class="text-body2 text-medium-emphasis mb-3">
            <template v-if="cancelBookingTarget?.state === 'OFFERED'">
              This withdraws the offer. They haven't accepted yet, so there's no calendar invite to cancel.
            </template>
            <template v-else>
              This sends a calendar cancellation for everything they were committed to, notifies them in the app,
              and keeps the record — with any fee below — visible on this position going forward.
            </template>
          </p>
          <v-alert v-if="cancelBookingTarget?.sharedDayId" type="warning" variant="tonal" density="compact" class="mb-3">
            This booking is part of a shared day. Cancelling it unlinks the day: the other booking goes back to its own
            day rate and its estimate changes.
          </v-alert>
          <v-textarea v-model="cancelReason" label="Reason (optional)" rows="2" class="mb-3" />
          <template v-if="cancelBookingTarget?.state === 'ACCEPTED' || cancelBookingTarget?.state === 'BOOKED'">
            <v-row dense class="mb-2">
              <v-col cols="6">
                <v-text-field v-model.number="cancelFeeAmount" label="Fee" type="number" prefix="$" density="compact" hide-details />
              </v-col>
              <v-col cols="6">
                <v-select v-model="cancelFeeStatus" :items="['WAIVED', 'OWED', 'PAID']" label="Status" density="compact" hide-details />
              </v-col>
            </v-row>
            <v-btn
              v-if="cancelBookingTarget"
              size="small"
              variant="text"
              class="mb-2"
              @click="openPersonHistoryFromCancel"
            >
              <v-icon start size="small">mdi-history</v-icon>
              View {{ personDisplayName(cancelBookingTarget) }}'s history
            </v-btn>
          </template>
          <div class="text-right mt-2">
            <v-btn text @click="showCancelBookingDialog = false">Back</v-btn>
            <v-btn color="error" :loading="cancellingId === cancelBookingTarget?.id" @click="submitCancelBooking">
              {{ cancelBookingTarget?.state === 'OFFERED' ? 'Pull Offer' : 'Cancel Booking' }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <LinkSharedDayDialog
      v-if="linkTarget && gig"
      v-model="showLinkDialog"
      :org-id="orgId()"
      :booking="linkTarget.booking"
      :position="linkTarget.position"
      :calls="calls"
      :time-zone="costZone"
      :person-name="personDisplayName(linkTarget.booking)"
      @linked="onSharedDayLinked"
    />

    <DuplicateGigDialog
      v-if="gig"
      v-model="showDuplicateDialog"
      :org-id="orgId()"
      :gig-id="gig.id"
      @duplicated="onGigDuplicated"
    />

    <PersonHistoryDialog
      v-model="showPersonHistoryDialog"
      :org-id="orgId()"
      :person-id="personHistoryPersonId"
      :person-name="personHistoryPersonName"
    />

    <!-- Send Offer preview — shows exactly what the candidate will see in
         My Offers before it's actually sent. -->
    <v-dialog v-model="showOfferPreviewDialog" width="480">
      <v-card>
        <v-card-title>Send Offer Preview</v-card-title>
        <v-card-text>
          <p class="text-body2 mb-2">
            {{ previewBooking ? personDisplayName(previewBooking) : '' }} will see:
          </p>
          <v-card variant="outlined" class="mb-4">
            <v-card-text>
              <p class="font-weight-medium mb-1">{{ gig?.name }} — {{ previewPosition?.role }}</p>
              <p class="text-body2 mb-1" v-if="previewBooking">
                Rate: {{ rateLabel(previewBookingRate.rate, previewBookingRate.rateType) }}
              </p>
              <template v-if="previewCalls.length">
                <p v-for="c in previewCalls" :key="c.id" class="text-body2 mb-1">
                  {{ c.type }} — {{ formatCallSpan(c) }}<template v-if="c.venueId"> — {{ venueLabel(c.venueId) }}</template>
                </p>
                <p class="text-body2 font-weight-medium mb-1">
                  Estimated Compensation: ${{ previewOfferTotal.toLocaleString() }}
                </p>
              </template>
              <p v-else class="text-body2 mb-1">
                Day: {{ previewPosition?.scopeDate ? formatDate(previewPosition.scopeDate, gigZone) : 'TBD' }}
                <template v-if="previewPosition?.venueId"> — {{ venueLabel(previewPosition.venueId) }}</template>
              </p>
              <p class="text-caption text-medium-emphasis mb-1">{{ previewTermsText }}</p>
              <p class="text-caption text-medium-emphasis mt-2 mb-0">
                This offer is for the full day/venue/role above at the agreed rate — any call times shown
                are what's currently planned, not a hard cap, and may shift as the gig firms up.
              </p>
            </v-card-text>
          </v-card>
          <div class="text-right">
            <v-btn text @click="showOfferPreviewDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="sendingOfferId === previewBooking?.id" @click="confirmSendOffer">
              Send Offer
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Budget override — owner/admin/manager only; shown when Send Offer
         would push a position's assigned total over its maxBudget and no
         automatic budget-fill (coordinator's same-call offset) applies. -->
    <v-dialog v-model="showOverrideDialog" width="450">
      <v-card>
        <v-card-title>Over Budget</v-card-title>
        <v-card-text>
          <p class="text-body2 mb-4">
            This offer puts <strong>{{ overrideInfo?.role }}</strong> at
            ${{ overrideInfo?.actualOfferedCost.toFixed(2) }} against a budget of
            ${{ overrideInfo?.budgeted.toFixed(2) }}. Overriding sends the offer anyway and is
            logged to the activity log.
          </p>
          <v-textarea
            v-model="overrideReason"
            label="Reason (required)"
            rows="2"
            autofocus
          />
          <div class="text-right mt-2">
            <v-btn text @click="showOverrideDialog = false">Cancel</v-btn>
            <v-btn
              color="warning"
              :loading="overriding"
              :disabled="!overrideReason.trim()"
              @click="confirmOverride"
            >
              Send Anyway
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useGigsStore } from '../stores/gigs';
import { usePeopleStore } from '../stores/people';
import { useRateCardsStore } from '../stores/rateCards';
import { useClientsStore } from '../stores/clients';
import { useVenuesStore } from '../stores/venues';
import { rescheduleGig } from '@gigdriver/client';
import { useItemsStore } from '../stores/items';
import { useQuotesStore } from '../stores/quotes';
import { useContractsStore } from '../stores/contracts';
import { useAuthStore } from '../stores/auth';
import { useOrganizationStore } from '../stores/organization';
import { useTimesheetsStore } from '../stores/timesheets';
import { useOverageStore } from '../stores/overage';
import { getOrgGigs, assignPerson, sendOffer, unassignPerson, respondToOffer, setBookingCallIds, updateBooking, cancelBooking, logActivity } from '@gigdriver/client';
import { fromZonedInput, toZonedInput, zonedDateStr, formatZonedDate, formatZonedTime, startOfDayIn, planGigReschedule, resolveCostZone, isValidTransition, summarizeBudget, summarizeActuals, buildSharedCosts, sharedTotal, computeOverage, overageStatus, preInvoiceConcerns, costForBooking, resolveDefaultRate, resolveAssignRate } from '@gigdriver/domain';
import type { Call, Position, Booking, Person, RateEntry, QuoteLineItem, BudgetPositionSummary, CancellationFeeStatus } from '@gigdriver/domain';
import PersonHistoryDialog from '../components/PersonHistoryDialog.vue';
import DuplicateGigDialog from '../components/DuplicateGigDialog.vue';
import GigTimesheet from '../components/GigTimesheet.vue';
import OverageCard from '../components/OverageCard.vue';
import PayrollCard from '../components/PayrollCard.vue';
import SharedDayPanel from '../components/SharedDayPanel.vue';
import LinkSharedDayDialog from '../components/LinkSharedDayDialog.vue';
import { useSharedDaysStore } from '../stores/sharedDays';
import type { SharedDay, SharedMemberData } from '@gigdriver/domain';

const route = useRoute();
const router = useRouter();
const gigsStore = useGigsStore();
const timesheetsStore = useTimesheetsStore();
const overageStore = useOverageStore();
const sharedStore = useSharedDaysStore();
const overageCardRef = ref<InstanceType<typeof OverageCard> | null>(null);
const peopleStore = usePeopleStore();
const rateCardsStore = useRateCardsStore();
const clientsStore = useClientsStore();
const venuesStore = useVenuesStore();
const itemsStore = useItemsStore();
const quotesStore = useQuotesStore();
const contractsStore = useContractsStore();
const authStore = useAuthStore();
const orgStore = useOrganizationStore();

const gig = computed(() => gigsStore.currentGig);
const calls = computed(() => gigsStore.calls);
const allBookings = computed(() => Object.values(gigsStore.bookingsByPosition).flat());
// The Timesheets section only matters once someone is actually committed.
const hasCommittedBookings = computed(() =>
  allBookings.value.some(b => b.state === 'ACCEPTED' || b.state === 'BOOKED' || b.state === 'COMPLETED'),
);
// Venues are referenced by id everywhere; pickers show the name.
const venueItems = computed(() => venuesStore.venues.map(v => ({ title: v.name, value: v.id })));
function venueLabel(id?: string): string {
  return venuesStore.nameFor(id) ?? '—';
}

// ---- time zones ----
// A gig's date and its calls' times are shown and edited in the VENUE's zone
// (the main venue's Venue.timeZone for the gig, a call's own venue for the
// call), never the viewer's browser zone. A venue without a zone falls back to
// the org zone, then the browser's.
const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
function zoneForVenue(venueId: string | undefined, fallback: string): string {
  return venuesStore.zoneFor(venueId) ?? fallback;
}
const gigZone = computed(() => zoneForVenue(gig.value?.venueId, orgStore.currentOrg?.timezone ?? browserZone));
// The zone COSTING groups days in. A day rate is one charge per calendar day,
// so this must be the zone the call times are shown in (the gig's main venue,
// then the org) — never UTC by accident: with no org zone set, an evening call
// used to land on the next UTC day and double a day rate. `source: 'default'`
// means neither zone is set and UTC is being used as a last resort (warned below).
const costZoneInfo = computed(() => resolveCostZone(venuesStore.zoneFor(gig.value?.venueId), orgStore.currentOrg?.timezone));
const costZone = computed(() => costZoneInfo.value.zone);
function callZone(call: Call): string {
  return call.venueId ? zoneForVenue(call.venueId, gigZone.value) : gigZone.value;
}

// Shows a date range when the gig's calls span more than one calendar day
// (multi-day gigs), otherwise the single gig date as before.
const gigDateDisplay = computed(() => {
  if (!gig.value) return '';
  const zone = gigZone.value;
  if (calls.value.length === 0) return formatDate(gig.value.date, zone);
  const days = [...new Set(calls.value.map(c => zonedDateStr(new Date(c.startTime), callZone(c))))].sort();
  if (days.length <= 1) return formatDate(gig.value.date, zone);
  // Each day key is already a calendar day in the call's own zone; format it
  // from noon UTC in UTC so no zone shifts it onto a neighbouring day.
  const label = (ymd: string) => formatZonedDate(new Date(`${ymd}T12:00:00Z`), 'UTC');
  return `${label(days[0])} – ${label(days[days.length - 1])}`;
});

const opError = ref<string | null>(null);
const error = computed<string | null>({
  get: () => opError.value ?? gigsStore.error,
  set: v => {
    opError.value = v;
    if (!v) gigsStore.error = null;
  },
});

const availableStates = ['DRAFT', 'QUOTED', 'BOOKED', 'COMPLETED', 'INVOICED', 'PAID', 'CANCELLED'];

function positionsForCall(callId: string): Position[] {
  return gigsStore.positions.filter(p => p.callIds.includes(callId));
}
// People whose booking actually covers this call — either directly
// (callIds includes it) or as a call-less, day-scoped commitment (empty
// callIds) under a position that's linked to it. Includes OFFERED (pending
// response) alongside actually-booked people, so a coordinator can see who
// they're waiting to hear back from, not just who's confirmed.
function bookedPeopleForCall(callId: string, positionId: string): Array<{ person: Person; booking: Booking }> {
  const bookings = gigsStore.bookingsByPosition[positionId] ?? [];
  return bookings
    .filter(
      b =>
        (b.state === 'OFFERED' || b.state === 'ACCEPTED' || b.state === 'BOOKED') &&
        (b.callIds.length === 0 || b.callIds.includes(callId)),
    )
    .map(b => {
      const person = peopleStore.people.find(p => p.id === b.personId);
      return person ? { person, booking: b } : undefined;
    })
    .filter((entry): entry is { person: Person; booking: Booking } => Boolean(entry));
}
// A booking with no callIds covers a call only via the "held for the whole
// day" fallback above — it has no per-call calendar invite (only the
// day-hold placeholder, once accepted), so showing it identically to a
// person actually scheduled on this specific call would be misleading.
// Lets a coordinator resolve it in one click instead of hunting for the
// separate "Calls" editor on the booking.
async function addBookingToCall(entry: { person: Person; booking: Booking }, pos: Position, callId: string) {
  if (!gig.value) return;
  try {
    error.value = null;
    await setBookingCallIds(orgId(), gig.value.id, pos, entry.booking, [...entry.booking.callIds, callId]);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to add this call';
  }
}
function positionCalls(pos: Position): Call[] {
  const callById = new Map(calls.value.map(c => [c.id, c]));
  return pos.callIds.map(id => callById.get(id)).filter((c): c is Call => Boolean(c));
}
function bookingCalls(b: Booking): Call[] {
  const callById = new Map(calls.value.map(c => [c.id, c]));
  return b.callIds.map(id => callById.get(id)).filter((c): c is Call => Boolean(c));
}
function personContact(b: Booking): string {
  const person = peopleStore.people.find(p => p.id === b.personId);
  return [person?.email, person?.phone].filter(Boolean).join(' · ');
}
const positionBookings = computed(() => gigsStore.bookingsByPosition);
// Cancelled/declined bookings stay visible forever (the whole point of
// tracking a real CANCELLED state) but sink to the bottom, muted, so an
// otherwise-fully-staffed position doesn't read as over-capacity at a
// glance just because history has piled up under it.
const TERMINAL_INACTIVE_STATES = new Set(['CANCELLED', 'DECLINED']);
function sortedPositionBookings(positionId: string): Booking[] {
  const bookings = positionBookings.value[positionId] ?? [];
  return [...bookings].sort((a, b) => {
    const aInactive = TERMINAL_INACTIVE_STATES.has(a.state) ? 1 : 0;
    const bInactive = TERMINAL_INACTIVE_STATES.has(b.state) ? 1 : 0;
    return aInactive - bInactive;
  });
}

// ---- shared days (Planning/SharedDay.md) ----
// A person booked on this gig AND another the same day can be linked so the day
// is priced once. This gig's members come from the live stores; members on other
// gigs are fetched (sharedStore.loadSiblings) when the link set changes.
const gigDays = computed<SharedDay[]>(() => (gig.value ? sharedStore.daysForGig(gig.value.id) : []));
const sharedMemberData = computed(() => {
  const map = new Map<string, SharedMemberData>();
  const gid = gig.value?.id;
  const ownBookings = Object.values(gigsStore.bookingsByPosition).flat();
  for (const day of gigDays.value) {
    for (const m of day.members) {
      if (m.gigId === gid) {
        const booking = ownBookings.find(b => b.id === m.bookingId);
        const position = gigsStore.positions.find(p => p.id === m.positionId);
        if (booking && position) map.set(m.bookingId, { booking, position, calls: calls.value, entries: timesheetsStore.gigEntries });
      } else {
        const sibling = sharedStore.siblings.get(m.bookingId);
        if (sibling) map.set(m.bookingId, sibling);
      }
    }
  }
  return map;
});
const sharedCosts = computed(() =>
  gigDays.value.length
    ? buildSharedCosts(gigDays.value, sharedMemberData.value, rateCardsStore.rateCards, costZone.value, orgStore.currentOrg?.holidays)
    : undefined,
);
const sharedDayOf = (b: Booking) => (b.sharedDayId ? gigDays.value.find(d => d.id === b.sharedDayId) ?? null : null);

const budgetSummaries = computed(() =>
  summarizeBudget(
    gigsStore.positions,
    gigsStore.bookingsByPosition,
    calls.value,
    rateCardsStore.rateCards,
    costZone.value,
    orgStore.currentOrg?.holidays,
    sharedCosts.value?.estimate,
  ),
);
// Actual labor cost from approved timesheet entries (read-only, coordinator+).
// Timesheet entries are subscribed here (not in GigTimesheet) because the
// Staffing Budget below needs them whether or not that section is mounted.
const actuals = computed(() =>
  summarizeActuals(
    gigsStore.positions,
    gigsStore.bookingsByPosition,
    timesheetsStore.gigEntries,
    calls.value,
    rateCardsStore.rateCards,
    costZone.value,
    orgStore.currentOrg?.holidays,
    sharedCosts.value,
  ),
);
function actualsForPosition(positionId: string) {
  return actuals.value.positions.find(p => p.positionId === positionId);
}
const canManagePayroll = computed(() => ['owner', 'admin', 'manager'].includes(orgStore.myRole ?? ''));
const hasActuals = computed(() => actuals.value.totals.approvedCost > 0 || actuals.value.totals.projectedCost > 0);
const budgetTotals = computed(() =>
  budgetSummaries.value.reduce(
    (totals, s) => ({ budgeted: totals.budgeted + s.budgeted, assigned: totals.assigned + s.actualOfferedCost }),
    { budgeted: 0, assigned: 0 },
  ),
);
function budgetGroupForPosition(positionId: string): BudgetPositionSummary | undefined {
  return budgetSummaries.value.find(s => s.positionId === positionId);
}
function isOverBudget(positionId: string): boolean {
  return budgetGroupForPosition(positionId)?.overBudget ?? false;
}

const orgId = () => route.query.org as string;

// Back to the gigs list as it was left (page, page size, sort live in its URL):
// step back through history when the previous entry is the list itself, and
// fall back to a fresh list when this page was opened directly or from
// elsewhere (a bookmark, a duplicate, another gig).
function backToGigs() {
  const previous = router.options.history.state.back;
  if (typeof previous === 'string' && /^\/gigs(\?|$)/.test(previous)) {
    router.back();
  } else {
    router.push({ name: 'GigsList', query: { org: orgId() } });
  }
}

const showDuplicateDialog = ref(false);
function onGigDuplicated(newGigId: string) {
  router.push({ path: `/gigs/${newGigId}`, query: { org: orgId() } });
}

function clientName(clientId?: string): string {
  if (gig.value?.clientName) return gig.value.clientName;
  if (!clientId) return '—';
  return clientsStore.clients.find(c => c.id === clientId)?.name ?? clientId;
}

// ---- Quote ----
const quote = computed(() => quotesStore.currentQuote);
const parentQuote = computed(() => quotesStore.parentQuote);
const laborLines = computed(() => quote.value?.lineItems.filter(l => l.source === 'labor') ?? []);
const itemLines = computed(() => quote.value?.lineItems.filter(l => l.source !== 'labor') ?? []);
// An APPROVED quote is read-only — editing goes through "Create Change
// Order" instead of mutating the committed quote in place.
const quoteEditable = computed(() => !quote.value || quote.value.state !== 'APPROVED');
const changeOrderDelta = computed(() =>
  quote.value ? round2((quote.value.total ?? 0) - (parentQuote.value?.total ?? 0)) : 0,
);

const taxRateInput = ref<number | undefined>(undefined);
const depositInput = ref<number | undefined>(undefined);
watch(quote, q => {
  taxRateInput.value = q?.taxRate;
  depositInput.value = q?.depositAmount;
});

// Falls back to the org default when no per-gig override is set.
const effectiveMarginPct = computed(() => gig.value?.laborMarginPctOverride ?? orgStore.currentOrg?.defaultLaborMarginPct ?? 0);
const marginPctInput = ref<number | undefined>(undefined);
watch(gig, g => {
  marginPctInput.value = g?.laborMarginPctOverride;
});

async function saveMarginPct() {
  if (!gig.value) return;
  await gigsStore.updateGig(orgId(), gig.value.id, { laborMarginPctOverride: marginPctInput.value || undefined });
}

async function recomputeQuoteLabor() {
  if (!gig.value) return;
  await quotesStore.recomputeLaborFromBudget(
    orgId(),
    gig.value.id,
    gig.value.clientId,
    gigsStore.positions,
    gigsStore.bookingsByPosition,
    calls.value,
    rateCardsStore.rateCards,
    effectiveMarginPct.value,
    costZone.value,
    orgStore.currentOrg?.holidays,
    sharedCosts.value?.estimate,
  );
}

async function handleSendQuote() {
  if (!gig.value) return;
  await quotesStore.sendQuote(orgId(), gig.value.id);
  if (isValidTransition(gig.value.state, 'QUOTED')) {
    await gigsStore.updateGig(orgId(), gig.value.id, { state: 'QUOTED' });
  }
}

async function saveTaxRate() {
  if (!gig.value) return;
  await quotesStore.setTaxRate(orgId(), gig.value.id, taxRateInput.value || undefined);
}

async function saveDeposit() {
  if (!gig.value) return;
  await quotesStore.setDeposit(orgId(), gig.value.id, depositInput.value || undefined);
}

async function handleMarkApproved() {
  if (!gig.value) return;
  await quotesStore.markApproved(orgId(), gig.value.id, authStore.currentUser?.uid ?? 'unknown');
  if (isValidTransition(gig.value.state, 'BOOKED')) {
    await gigsStore.updateGig(orgId(), gig.value.id, { state: 'BOOKED' });
  }
}

async function handleMarkDeclined() {
  if (!gig.value) return;
  await quotesStore.markDeclined(orgId(), gig.value.id);
  if (isValidTransition(gig.value.state, 'DRAFT')) {
    await gigsStore.updateGig(orgId(), gig.value.id, { state: 'DRAFT' });
  }
}

async function handleStartChangeOrder() {
  if (!gig.value) return;
  if (!confirm('Create a change order? The approved quote stays as the committed record; this starts an editable copy on top of it.')) {
    return;
  }
  await quotesStore.startChangeOrder(orgId(), gig.value.id);
}

// ---- Contract (tokenized e-sign) — independent of the Quote/Gig state
// machine by design: signing only records Contract.state, it never
// advances the gig on its own. Staff still clicks "Mark Approved" above,
// exactly as before this feature existed.
const contract = computed(() => contractsStore.currentContract);

async function handleSendContract() {
  if (!gig.value) return;
  await contractsStore.sendForSignature(orgId(), gig.value.id);
  if (isValidTransition(gig.value.state, 'QUOTED')) {
    await gigsStore.updateGig(orgId(), gig.value.id, { state: 'QUOTED' });
  }
}

async function handleDownloadContractPdf() {
  if (!gig.value || !contract.value) return;
  const url = await contractsStore.downloadPdf(orgId(), gig.value.id, contract.value.id);
  window.open(url, '_blank', 'noopener');
}

const resolvedClient = computed(() =>
  gig.value ? clientsStore.clients.find(c => c.id === gig.value!.clientId) : undefined,
);

// Invoicing doesn't require the gig to be COMPLETED — a deposit can be
// invoiced as soon as the quote is approved; Invoice Ninja tracks the
// deposit-due-now / balance-due-later split on that one invoice.
const canCreateInvoice = computed(() => !!resolvedClient.value?.invoiceNinjaClientId);

const createInvoiceHint = computed(() => {
  if (!gig.value) return '';
  if (!resolvedClient.value?.invoiceNinjaClientId) {
    return 'This client has not been pushed to Invoice Ninja yet — push it from the Clients page.';
  }
  return '';
});

const invoiceUrl = computed(() => {
  const base = orgStore.currentOrg?.invoiceNinjaBaseUrl;
  const id = quote.value?.invoiceNinjaInvoiceId;
  return base && id ? `${base}/invoices/${id}` : '#';
});

async function handleCreateInvoice() {
  if (!gig.value) return;
  // A confirm, never a block: raised only once the gig is completed (a deposit
  // invoice before then is legitimate) and only for things that are easy to
  // miss — undecided labor overage, unreviewed time, unfinalized payroll.
  const overage = computeOverage(actuals.value, effectiveMarginPct.value);
  const concerns = preInvoiceConcerns({
    gigState: gig.value.state,
    overage: { status: overageStatus(overage, overageStore.decisions).status, overageCost: overage.overageCost },
    hasApprovedTime: actuals.value.totals.approvedCost > 0,
    unreviewedEntries: actuals.value.totals.openCount + actuals.value.totals.pendingCount,
    payrollState: gig.value.payrollState,
  });
  if (concerns.length > 0 && !confirm(`Before invoicing:\n\n• ${concerns.join('\n• ')}\n\nCreate the invoice anyway?`)) return;
  await quotesStore.createInvoice(orgId(), gig.value.id);
}

const paymentStatusMsg = ref<string | null>(null);

async function handleRefreshStatus() {
  if (!gig.value) return;
  paymentStatusMsg.value = null;
  try {
    const r = await quotesStore.refreshInvoiceStatus(orgId(), gig.value.id);
    paymentStatusMsg.value = r.fullyPaid
      ? 'Paid in full.'
      : `Balance due: $${r.balance.toFixed(2)}.`;
  } catch {
    // quotesStore.error is set
  }
}

async function removeQuoteLine(lineId: string) {
  if (!gig.value) return;
  await quotesStore.removeLine(orgId(), gig.value.id, lineId);
}

const showAddItemDialog = ref(false);
const addItemForm = reactive({ itemId: '', quantity: 1 });

function openAddItemLine() {
  addItemForm.itemId = '';
  addItemForm.quantity = 1;
  showAddItemDialog.value = true;
}

async function saveAddItemLine() {
  if (!gig.value) return;
  const item = itemsStore.items.find(i => i.id === addItemForm.itemId);
  if (!item) return;
  await quotesStore.addItemLine(orgId(), gig.value.id, gig.value.clientId, item, addItemForm.quantity || 1);
  showAddItemDialog.value = false;
}

const showAddManualDialog = ref(false);
const manualForm = reactive({ description: '', quantity: 1, unitCost: 0 });

function openAddManualLine() {
  manualForm.description = '';
  manualForm.quantity = 1;
  manualForm.unitCost = 0;
  showAddManualDialog.value = true;
}

async function saveAddManualLine() {
  if (!gig.value) return;
  await quotesStore.addManualLine(
    orgId(),
    gig.value.id,
    gig.value.clientId,
    manualForm.description,
    manualForm.quantity || 1,
    manualForm.unitCost || 0,
  );
  showAddManualDialog.value = false;
}

const showEditLineDialog = ref(false);
const editingLine = ref<QuoteLineItem | null>(null);
const editLineForm = reactive({ quantity: 1, unitCost: 0 });

function openEditLine(line: QuoteLineItem) {
  editingLine.value = line;
  editLineForm.quantity = line.quantity;
  editLineForm.unitCost = line.unitCost;
  showEditLineDialog.value = true;
}

async function saveEditLine() {
  if (!gig.value || !editingLine.value) return;
  await quotesStore.updateLine(orgId(), gig.value.id, editingLine.value.id, {
    quantity: editLineForm.quantity,
    unitCost: editLineForm.unitCost,
  });
  showEditLineDialog.value = false;
}

onMounted(async () => {
  const gigId = route.params.gigId as string;
  if (!orgId() || !gigId) return;
  if (orgStore.currentOrg?.id !== orgId()) {
    await orgStore.selectOrganization(orgId());
  }
  // Staff can't read people/rateCards/clients/items/quotes (firestore.rules
  // excludes them — pricing and pay-rate detail); coordinators can't read
  // clients/items/quotes/contracts either, and an unfiltered bookings
  // listener under each position is denied outright for them too (see
  // WatchGigDetailOptions.skipBookings) — skip all of it rather than firing
  // requests that only fail and, for bookings, surface a visible error.
  gigsStore.subscribeGigDetail(orgId(), gigId, { skipBookings: orgStore.isStaff });
  // Every member reads venues (names + time zones for display).
  venuesStore.loadOrgVenues(orgId());
  // Coordinator+ run staffing: people, rate cards, timesheets.
  if (orgStore.isCoordinatorPlus) {
    peopleStore.loadOrgPeople(orgId());
    rateCardsStore.loadOrgRateCards(orgId());
    timesheetsStore.watchGig(orgId(), gigId);
    sharedStore.watchOrg(orgId(), () => sharedStore.loadSiblings(orgId(), gigId));
    getOrgGigs(orgId()).then(list => (gigNames.value = Object.fromEntries(list.map(g => [g.id, g.name])))).catch(() => {});
  }
  // Client pricing — clients, item catalog, quotes, contracts, overage — is
  // manager+ only (firestore.rules); coordinators never request it.
  if (orgStore.isManagerPlus) {
    clientsStore.loadOrgClients(orgId());
    itemsStore.loadOrgItems(orgId());
    quotesStore.loadQuote(orgId(), gigId);
    contractsStore.loadContract(orgId(), gigId);
    overageStore.watchGig(orgId(), gigId);
  }
});
onUnmounted(() => {
  gigsStore.unsubscribeGigDetail();
  timesheetsStore.stopGigWatch();
  sharedStore.stopWatching();
  overageStore.stopWatching();
});

// ---- date/time helpers (local time, not ISO) ----
function pad(n: number) {
  return String(n).padStart(2, '0');
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
function toInput(d: Date, zone: string = gigZone.value): string {
  return toZonedInput(d, zone);
}
function toDateStr(d: Date, zone: string = gigZone.value): string {
  return zonedDateStr(d, zone);
}
function addH(d: Date, h: number): Date {
  return new Date(d.getTime() + h * 3_600_000);
}
// `hour`:00 on the gig's date, as a datetime-local string in `zone`.
function gigDateAt(hour: number, zone: string = gigZone.value): string {
  const day = gig.value?.date ? zonedDateStr(new Date(gig.value.date), gigZone.value) : zonedDateStr(new Date(), zone);
  return `${day}T${pad(hour)}:00`;
}

// `zone` given = a venue-local calendar day/time; omitted = a moment in the
// viewer's own zone (approval/signature/cancel timestamps).
function formatDate(date: any, zone?: string): string {
  return formatZonedDate(new Date(date), zone ?? browserZone);
}
function formatTime(time: any, zone?: string, withZoneName = false): string {
  return formatZonedTime(new Date(time), zone ?? browserZone, withZoneName);
}
function formatCallSpan(call: Call): string {
  const zone = callZone(call);
  const start = new Date(call.startTime);
  const end = new Date(call.endTime);
  const sameDay = toDateStr(start, zone) === toDateStr(end, zone);
  return sameDay
    ? `${formatDate(start, zone)}, ${formatTime(start, zone)} - ${formatTime(end, zone, true)}`
    : `${formatDate(start, zone)}, ${formatTime(start, zone)} - ${formatDate(end, zone)}, ${formatTime(end, zone, true)}`;
}
function rateLabel(rate: RateEntry, rateType?: 'day' | 'hourly'): string {
  const suffix = rateType === 'hourly' ? '/hr' : rateType === 'day' ? '/day' : '';
  return `$${rate.amount}${suffix}`;
}

// ---- Gig edit ----
const showGigDialog = ref(false);
const savingGig = ref(false);
const gigForm = reactive({
  name: '',
  clientId: '',
  date: '',
  venueId: '',
  description: '',
  state: 'DRAFT',
});
// The zone the (possibly just-changed) main venue implies; a venue that was
// never given one keeps the gig's current zone.
const gigFormZone = computed(() => zoneForVenue(gigForm.venueId, gigZone.value));

function openEditGig() {
  if (!gig.value) return;
  gigForm.name = gig.value.name;
  gigForm.clientId = gig.value.clientId ?? '';
  gigForm.date = gig.value.date ? toDateStr(new Date(gig.value.date)) : '';
  gigForm.venueId = gig.value.venueId ?? '';
  gigForm.description = gig.value.description ?? '';
  gigForm.state = gig.value.state;
  showGigDialog.value = true;
}

// Moving the gig date: offer to move the calls with it (prompting first —
// it re-sends invites to confirmed people). Computed when Save is pressed.
const showRescheduleDialog = ref(false);
const reschedule = reactive({
  dayOffset: 0,
  fromLabel: '',
  toLabel: '',
  callCount: 0,
  positionDays: 0,
  affectedPeople: 0,
  blockedReason: '',
});

const RESCHEDULE_LOCKED_STATES = ['COMPLETED', 'INVOICED', 'PAID', 'CANCELLED'];

function prepareReschedule(newDate: string) {
  if (!gig.value) return;
  const plan = planGigReschedule({
    gig: gig.value,
    calls: calls.value,
    positions: gigsStore.positions,
    newDate,
    timeZoneFor: id => venuesStore.zoneFor(id),
    timeZone: gigZone.value,
  });
  reschedule.dayOffset = plan.dayOffset;
  reschedule.fromLabel = formatDate(gig.value.date, gigZone.value);
  reschedule.toLabel = formatDate(plan.gigDate, gigZone.value);
  reschedule.callCount = plan.calls.length;
  reschedule.positionDays = plan.positions.length;
  reschedule.affectedPeople = new Set(
    allBookings.value.filter(b => b.state === 'OFFERED' || b.state === 'ACCEPTED' || b.state === 'BOOKED').map(b => b.personId),
  ).size;
  reschedule.blockedReason = gig.value.payrollState
    ? 'Payroll for this gig is already processed, so its calls are locked. You can still change the date only.'
    : RESCHEDULE_LOCKED_STATES.includes(gig.value.state)
      ? `This gig is ${gig.value.state.toLowerCase()}, so its calls stay where they are. You can still change the date only.`
      : '';
}

async function saveGig() {
  if (!gig.value) return;
  if (!gigForm.venueId) {
    error.value = 'A main venue is required';
    return;
  }
  const dateChanged = !!gigForm.date && gigForm.date !== toDateStr(new Date(gig.value.date));
  const hasSchedule = calls.value.length > 0 || gigsStore.positions.some(p => !!p.scopeDate);
  if (dateChanged && hasSchedule) {
    prepareReschedule(gigForm.date);
    showRescheduleDialog.value = true;
    return;
  }
  await saveGigWith(false);
}

// `moveCalls` true = also shift every call by the date delta (see rescheduleGig).
async function saveGigWith(moveCalls: boolean) {
  if (!gig.value) return;
  try {
    savingGig.value = true;
    error.value = null;
    const oldZone = gigZone.value;
    const newZone = gigFormZone.value;
    const dateChanged = !!gigForm.date && gigForm.date !== toDateStr(new Date(gig.value.date));

    // Changing the main venue must not silently move calls that never had a
    // venue of their own (they follow the gig's): pin them to the old venue.
    const oldVenueId = gig.value.venueId;
    if (oldVenueId && gigForm.venueId !== oldVenueId) {
      for (const c of calls.value.filter(c => !c.venueId)) {
        await gigsStore.updateCall(orgId(), gig.value.id, c.id, { venueId: oldVenueId });
      }
    }
    await gigsStore.updateGig(orgId(), gig.value.id, {
      name: gigForm.name,
      clientId: gigForm.clientId,
      // With "move calls", the batch below writes the date; otherwise the new
      // day is midnight in the venue's zone, not the browser's.
      ...((dateChanged || newZone !== oldZone) && !moveCalls ? { date: startOfDayIn(gigForm.date, newZone) } : {}),
      venueId: gigForm.venueId,
      description: gigForm.description || undefined,
      state: gigForm.state as any,
    });
    if (dateChanged && moveCalls) {
      await rescheduleGig(orgId(), gig.value.id, gigForm.date, id => venuesStore.zoneFor(id), newZone);
    }
    showRescheduleDialog.value = false;
    showGigDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to save gig';
  } finally {
    savingGig.value = false;
  }
}

const showNewClientDialog = ref(false);
const savingNewClient = ref(false);
const newClient = ref({ name: '', contactEmail: '', contactPhone: '' });

function openNewClient() {
  newClient.value = { name: '', contactEmail: '', contactPhone: '' };
  showNewClientDialog.value = true;
}

async function saveNewClient() {
  try {
    savingNewClient.value = true;
    const id = await clientsStore.createClient(orgId(), {
      name: newClient.value.name,
      contactEmail: newClient.value.contactEmail || undefined,
      contactPhone: newClient.value.contactPhone || undefined,
    });
    gigForm.clientId = id ?? '';
    showNewClientDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to add client';
  } finally {
    savingNewClient.value = false;
  }
}

// Tracks the last state actually committed to Firestore (not just what's
// shown in the v-select, which the model binding below mutates immediately
// on click) — needed to revert the dropdown if the user backs out of the
// "gig has active bookings" warning, or if the write itself fails.
let lastCommittedGigState: string | null = null;
watch(
  gig,
  g => {
    if (g && lastCommittedGigState === null) lastCommittedGigState = g.state;
  },
  { immediate: true },
);

async function updateGigState() {
  if (!gig.value) return;
  const previousState = lastCommittedGigState;

  if (gig.value.state === 'CANCELLED') {
    const activeCount = Object.values(gigsStore.bookingsByPosition)
      .flat()
      .filter(b => b.state === 'ACCEPTED' || b.state === 'BOOKED').length;
    if (activeCount > 0) {
      const proceed = confirm(
        `${activeCount} ${activeCount === 1 ? 'person is' : 'people are'} still actively booked on this gig. ` +
          `Cancelling the gig does NOT automatically cancel them or notify them — cancel each person individually ` +
          `first if you need to release them.\n\nCancel the gig anyway?`,
      );
      if (!proceed) {
        gig.value.state = (previousState ?? gig.value.state) as typeof gig.value.state;
        return;
      }
    }
  }

  if (gig.value.state === 'COMPLETED') {
    const { openCount, pendingCount } = actuals.value.totals;
    if (openCount + pendingCount > 0) {
      const parts = [
        openCount > 0 ? `${openCount} ${openCount === 1 ? 'person is' : 'people are'} still clocked in` : '',
        pendingCount > 0 ? `${pendingCount} timesheet ${pendingCount === 1 ? 'entry still needs' : 'entries still need'} review` : '',
      ].filter(Boolean);
      const proceed = confirm(`${parts.join(' and ')}. Unapproved time isn't counted in actual cost.\n\nMark the gig completed anyway?`);
      if (!proceed) {
        gig.value.state = (previousState ?? gig.value.state) as typeof gig.value.state;
        return;
      }
    }
  }

  try {
    error.value = null;
    await gigsStore.updateGig(orgId(), gig.value.id, { state: gig.value.state as any });
    lastCommittedGigState = gig.value.state;
    // Completing with labor over the gig's budget: offer the bill/absorb
    // decision now (the card's own dialog; "Decide later" dismisses it).
    if (gig.value.state === 'COMPLETED') overageCardRef.value?.promptDecision();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to update state';
    gig.value.state = (previousState ?? gig.value.state) as typeof gig.value.state;
  }
}

// ---- Standard calls ----
const showStandardDialog = ref(false);
const savingStd = ref(false);
const stdForm = reactive({
  venueId: '',
  show: { start: '', end: '' },
  setup: { start: '', end: '' },
  strike: { start: '', end: '' },
});

// Zone the Set Up Calls times are entered in: the chosen venue's.
const stdZone = computed(() => zoneForVenue(stdForm.venueId, gigZone.value));

function openStandardDialog() {
  stdForm.venueId = gig.value?.venueId ?? '';
  stdForm.show.start = gigDateAt(18);
  stdForm.show.end = gigDateAt(23);
  recomputeStd();
  showStandardDialog.value = true;
}

// Setup/strike derive from show; datetime-local strings are wall-clock in
// stdZone, so hour offsets are applied on real instants and re-read there.
function recomputeStd() {
  const zone = stdZone.value;
  const s = stdForm.show.start ? fromZonedInput(stdForm.show.start, zone) : null;
  const e = stdForm.show.end ? fromZonedInput(stdForm.show.end, zone) : null;
  if (s && !isNaN(s.getTime())) {
    stdForm.setup.start = toInput(addH(s, -3), zone);
    stdForm.setup.end = toInput(addH(s, -1), zone);
  }
  if (e && !isNaN(e.getTime())) {
    stdForm.strike.start = toInput(e, zone);
    stdForm.strike.end = toInput(addH(e, 2), zone);
  }
}
watch(() => [stdForm.show.start, stdForm.show.end, stdZone.value], recomputeStd);

async function createStandardCalls() {
  if (!gig.value) return;
  try {
    savingStd.value = true;
    error.value = null;
    const gigId = gig.value.id;
    for (const type of ['setup', 'show', 'strike'] as const) {
      await gigsStore.createCall(orgId(), gigId, {
        type,
        startTime: fromZonedInput(stdForm[type].start, stdZone.value),
        endTime: fromZonedInput(stdForm[type].end, stdZone.value),
        venueId: stdForm.venueId || undefined,
        gigId,
        orgId: orgId(),
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
    showStandardDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to create calls';
  } finally {
    savingStd.value = false;
  }
}

// ---- Add / edit call ----
const showCallDialog = ref(false);
const savingCall = ref(false);
const callEditingId = ref<string | null>(null);
const callForm = reactive({ type: 'show', startTime: '', endTime: '', venueId: '', notes: '' });

// Zone the call's times are entered in: its venue's (default: the gig's).
const callFormZone = computed(() => zoneForVenue(callForm.venueId, gigZone.value));

// A new call defaults to 6pm on the day of the show call (else the gig date),
// in the call's own zone.
function prefillCallTimes() {
  const showCall = calls.value.find(c => c.type === 'show');
  const zone = callFormZone.value;
  const day = showCall ? toDateStr(new Date(showCall.startTime), callZone(showCall)) : gigDateAt(18).slice(0, 10);
  callForm.startTime = `${day}T18:00`;
  callForm.endTime = toInput(addH(fromZonedInput(callForm.startTime, zone), 3), zone);
}

function openAddCall() {
  callEditingId.value = null;
  callForm.type = 'show';
  callForm.venueId = gig.value?.venueId ?? '';
  callForm.notes = '';
  prefillCallTimes();
  showCallDialog.value = true;
}

function openEditCall(call: Call) {
  callEditingId.value = call.id;
  callForm.type = call.type;
  callForm.startTime = toInput(new Date(call.startTime), callZone(call));
  callForm.endTime = toInput(new Date(call.endTime), callZone(call));
  callForm.venueId = call.venueId ?? '';
  callForm.notes = call.notes ?? '';
  showCallDialog.value = true;
}

async function saveCall() {
  if (!gig.value) return;
  const zone = callFormZone.value;
  if (fromZonedInput(callForm.endTime, zone) <= fromZonedInput(callForm.startTime, zone)) {
    error.value = 'End time must be after start time';
    return;
  }
  try {
    savingCall.value = true;
    error.value = null;
    const gigId = gig.value.id;
    const payload = {
      type: callForm.type as Call['type'],
      startTime: fromZonedInput(callForm.startTime, zone),
      endTime: fromZonedInput(callForm.endTime, zone),
      venueId: callForm.venueId || undefined,
      notes: callForm.notes || undefined,
    };
    if (callEditingId.value) {
      await gigsStore.updateCall(orgId(), gigId, callEditingId.value, payload);
    } else {
      await gigsStore.createCall(orgId(), gigId, {
        ...payload,
        gigId,
        orgId: orgId(),
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
    showCallDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to save call';
  } finally {
    savingCall.value = false;
  }
}

async function deleteCall(callId: string) {
  if (!gig.value) return;

  const affectedBookings = positionsForCall(callId)
    .flatMap(pos => positionBookings.value[pos.id] ?? [])
    .filter(b => b.callIds.includes(callId) && (b.state === 'DRAFT' || b.state === 'OFFERED' || b.state === 'ACCEPTED'));

  const message = affectedBookings.length
    ? `Delete this call? This will unlink it from ${[...new Set(affectedBookings.map(personDisplayName))].join(', ')}'s booking(s) — ` +
      `they'll remain booked for their other calls.`
    : 'Delete this call?';
  if (!confirm(message)) return;

  try {
    error.value = null;
    await gigsStore.deleteCall(orgId(), gig.value.id, callId);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to delete call';
  }
}

// ---- Add / edit position ----
const showPositionDialog = ref(false);
const savingPosition = ref(false);
const editingPositionId = ref<string | null>(null);
const positionForm = reactive({
  rateSource: 'manual' as 'manual' | 'card',
  rateCardId: '' as string,
  rateCardRole: '' as string,
  rateType: 'day' as 'day' | 'hourly',
  role: '',
  label: '',
  amount: 0,
  quantity: 1,
  maxBudget: 0,
  callIds: [] as string[],
  venueId: '',
  scopeDate: '',
});

const selectedCard = computed(() =>
  rateCardsStore.rateCards.find(c => c.id === positionForm.rateCardId),
);
const cardRoleNames = computed(() =>
  selectedCard.value ? Object.keys(selectedCard.value.roles) : [],
);
// Hourly positions: a starting point for Total Budget from the linked calls'
// hours, costed exactly like a booking (OT, minimums, Standard Timekeeping)
// at the position's rate, times quantity. Null until it's an hourly position
// with a rate and at least one linked call.
const suggestedBudget = computed<number | null>(() => {
  if (positionForm.rateType !== 'hourly' || !(positionForm.amount > 0) || positionForm.callIds.length === 0) return null;
  const fromCard = positionForm.rateSource === 'card';
  const perPerson = costForBooking(
    { callIds: positionForm.callIds, rate: { amount: positionForm.amount, currency: 'USD' }, rateType: 'hourly' } as Booking,
    {
      rate: { amount: positionForm.amount, currency: 'USD' },
      rateType: 'hourly',
      rateCardId: fromCard ? positionForm.rateCardId : undefined,
      rateCardRole: fromCard ? positionForm.rateCardRole : undefined,
    },
    calls.value,
    rateCardsStore.rateCards,
    costZone.value,
    orgStore.currentOrg?.holidays,
  );
  if (!(perPerson > 0)) return null;
  return Math.round(perPerson * Math.max(1, positionForm.quantity || 1) * 100) / 100;
});
const callSelectItems = computed(() =>
  calls.value.map(c => ({ title: `${c.type} — ${formatCallSpan(c)}`, value: c.id })),
);

watch(
  () => [positionForm.rateSource, positionForm.rateCardId, positionForm.rateCardRole, positionForm.rateType],
  () => {
    if (positionForm.rateSource !== 'card') return;
    const card = selectedCard.value;
    // Auto-pick the first role when none is selected yet, or the previous
    // selection doesn't exist on the newly-chosen card — the Role <v-select>
    // shows a lone option highlighted without actually committing it to the
    // model, so without this a single-role card would silently leave the
    // position with no role and a $0 rate. Re-assigning re-triggers this
    // same watcher, which then proceeds with the role now set.
    if (card && (!positionForm.rateCardRole || !card.roles[positionForm.rateCardRole])) {
      positionForm.rateCardRole = Object.keys(card.roles)[0] ?? '';
      return;
    }
    const roleRate = card && positionForm.rateCardRole ? card.roles[positionForm.rateCardRole] : undefined;
    if (!roleRate) return;
    positionForm.role = positionForm.rateCardRole;
    positionForm.amount =
      positionForm.rateType === 'hourly' ? roleRate.hourlyRate.amount : roleRate.dayRate.amount;
    positionForm.maxBudget = positionForm.amount;
  },
);

function openAddPosition() {
  editingPositionId.value = null;
  positionForm.rateSource = rateCardsStore.rateCards.length ? 'card' : 'manual';
  positionForm.rateCardId = rateCardsStore.rateCards[0]?.id ?? '';
  positionForm.rateCardRole = '';
  positionForm.rateType = 'day';
  positionForm.role = '';
  positionForm.label = '';
  positionForm.amount = 0;
  positionForm.quantity = 1;
  positionForm.maxBudget = 0;
  positionForm.callIds = [];
  positionForm.venueId = gig.value?.venueId ?? '';
  positionForm.scopeDate = '';
  showPositionDialog.value = true;
}

function openEditPosition(pos: Position) {
  editingPositionId.value = pos.id;
  positionForm.rateSource = pos.rateCardId ? 'card' : 'manual';
  positionForm.rateCardId = pos.rateCardId ?? (rateCardsStore.rateCards[0]?.id ?? '');
  positionForm.rateCardRole = pos.rateCardRole ?? '';
  positionForm.rateType = pos.rateType ?? 'day';
  positionForm.role = pos.role;
  positionForm.label = pos.label ?? '';
  positionForm.amount = pos.rate.amount;
  positionForm.quantity = pos.quantity;
  positionForm.maxBudget = pos.maxBudget ?? 0;
  positionForm.callIds = [...pos.callIds];
  positionForm.venueId = pos.venueId ?? '';
  positionForm.scopeDate = pos.scopeDate ? toDateStr(new Date(pos.scopeDate), gigZone.value) : '';
  showPositionDialog.value = true;
}

async function savePosition() {
  if (!gig.value) return;
  const unscheduled = positionForm.callIds.length === 0;
  // A call-less position's offers carry no date/time at all unless this is
  // set — without it, an accepted call-less booking has nothing for
  // syncDayHold to build a "hold this date" placeholder from (see the
  // warning it logs for exactly this gap), so the person's calendar would
  // show nothing at all for a day they're still committed to.
  if (unscheduled && !positionForm.scopeDate) {
    error.value = 'A day is required for a position with no linked calls.';
    return;
  }
  try {
    savingPosition.value = true;
    error.value = null;
    const gigId = gig.value.id;
    const fromCard = positionForm.rateSource === 'card';
    const payload = {
      role: fromCard ? positionForm.rateCardRole : positionForm.role,
      label: positionForm.label || undefined,
      quantity: positionForm.quantity,
      rate: { amount: positionForm.amount || 0, currency: 'USD' },
      rateType: positionForm.rateType,
      rateCardId: fromCard ? positionForm.rateCardId : undefined,
      rateCardRole: fromCard ? positionForm.rateCardRole : undefined,
      maxBudget: positionForm.maxBudget || undefined,
      callIds: [...positionForm.callIds],
      venueId: unscheduled ? positionForm.venueId || undefined : undefined,
      scopeDate: unscheduled && positionForm.scopeDate ? startOfDayIn(positionForm.scopeDate, gigZone.value) : undefined,
    };
    if (editingPositionId.value) {
      await gigsStore.updatePosition(orgId(), gigId, editingPositionId.value, payload);
    } else {
      await gigsStore.createPosition(orgId(), gigId, {
        ...payload,
        gigId,
        orgId: orgId(),
        state: 'OPEN',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
    showPositionDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to save position';
  } finally {
    savingPosition.value = false;
  }
}

async function deletePosition(pos: Position) {
  if (!gig.value) return;

  const activeBookings = (positionBookings.value[pos.id] ?? []).filter(
    b => b.state === 'DRAFT' || b.state === 'OFFERED' || b.state === 'ACCEPTED',
  );
  const message = activeBookings.length
    ? `Delete this position? This will also cancel the booking${activeBookings.length > 1 ? 's' : ''} for ` +
      `${[...new Set(activeBookings.map(personDisplayName))].join(', ')}.`
    : 'Delete this position?';
  if (!confirm(message)) return;

  try {
    error.value = null;
    await gigsStore.deletePosition(orgId(), gig.value.id, pos.id);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to delete position';
  }
}

// ---- Manage a position's linked calls ----
const showManageCallsDialog = ref(false);
const manageCallsPosition = ref<Position | null>(null);
const manageCallIds = ref<string[]>([]);
const savingManageCalls = ref(false);

function openManageCalls(pos: Position) {
  manageCallsPosition.value = pos;
  manageCallIds.value = [...pos.callIds];
  showManageCallsDialog.value = true;
}

async function saveManageCalls() {
  if (!gig.value || !manageCallsPosition.value) return;
  try {
    savingManageCalls.value = true;
    error.value = null;
    await gigsStore.setPositionCallIds(orgId(), gig.value.id, manageCallsPosition.value.id, manageCallIds.value);
    showManageCallsDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to update linked calls';
  } finally {
    savingManageCalls.value = false;
  }
}

// ---- Assign / offer ----
const showOfferDialog = ref(false);
const offerPosition = ref<Position | null>(null);
const offerPersonId = ref<string | null>(null);
const assignRate = ref(0);
const isOffering = ref(false);
const respondingId = ref<string | null>(null);
const sendingOfferId = ref<string | null>(null);
const unassigningId = ref<string | null>(null);

function bookingStateColor(state: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'grey',
    OFFERED: 'blue',
    ACCEPTED: 'success',
    DECLINED: 'red',
    BOOKED: 'green',
    COMPLETED: 'purple',
  };
  return colors[state] || 'grey';
}

// booking.personName is only denormalized once a booking is OFFERED — a
// still-DRAFT one has no name to show yet, so fall back to the roster.
function personDisplayName(booking: Booking): string {
  if (booking.personName) return booking.personName;
  return peopleStore.people.find(p => p.id === booking.personId)?.name ?? booking.personId;
}

// Which of the position's own linked calls to offer — the checkboxes in the
// Assign dialog, defaulted to all of them (leaving all unchecked offers
// just the day/venue/role, per the offer-timing decision).
const offerCallIds = ref<string[]>([]);
const offerPositionCalls = computed<Call[]>(() => {
  const pos = offerPosition.value;
  if (!pos) return [];
  const callById = new Map(calls.value.map(c => [c.id, c]));
  return pos.callIds.map(id => callById.get(id)).filter((c): c is Call => Boolean(c));
});
// assignPerson always mirrors the Position's own scopeDate onto the
// booking (no per-offer override) — so a call-less assignment with no
// position-level Day set would silently produce a dateless booking. The
// Position dialog now requires a Day whenever it has zero calls, but a
// position that DOES have calls can still end up here if every checkbox
// above gets unchecked for this one offer — that's the case this still
// needs to catch.
const offerMissingDate = computed(() => offerCallIds.value.length === 0 && !offerPosition.value?.scopeDate);

function rateCardForPosition(pos: Position) {
  return pos.rateCardId ? rateCardsStore.rateCards.find(rc => rc.id === pos.rateCardId) : undefined;
}

// Prefill order: the selected person's own default (People page), else the
// rate card's pay rate for this role, else the position's own (bill) rate —
// still just a starting point, editable either way.
const assignRateHint = computed(() => {
  const pos = offerPosition.value;
  if (offerPersonId.value && pos) {
    const person = peopleStore.people.find(p => p.id === offerPersonId.value);
    if (resolveDefaultRate(person, pos.role, pos.rateType) != null) {
      return `Pre-filled from ${person?.name}'s default rate for ${pos.role} — still editable`;
    }
  }
  if (pos) {
    const card = rateCardForPosition(pos);
    const roleRate = card && pos.rateCardRole ? card.roles[pos.rateCardRole] : undefined;
    const payRate = pos.rateType === 'hourly' ? roleRate?.payHourlyRate?.amount : roleRate?.payDayRate?.amount;
    if (payRate != null) {
      return 'Pre-filled from the rate card\'s pay rate for this role — still editable';
    }
  }
  return 'Defaults to the position\'s rate — override for this specific person';
});

watch(offerPersonId, personId => {
  const pos = offerPosition.value;
  if (!pos) return;
  const person = personId ? peopleStore.people.find(p => p.id === personId) : undefined;
  assignRate.value = resolveAssignRate(person, pos, rateCardForPosition(pos));
});

function openOfferDialog(pos: Position) {
  offerPosition.value = pos;
  offerPersonId.value = null;
  offerCallIds.value = [...pos.callIds];
  assignRate.value = resolveAssignRate(undefined, pos, rateCardForPosition(pos));
  showOfferDialog.value = true;
}

async function submitOffer() {
  if (!offerPosition.value || !offerPersonId.value || !gig.value) return;
  if (offerMissingDate.value) {
    error.value = 'This position has no Day set — edit the position to add one before assigning without a call.';
    return;
  }
  try {
    isOffering.value = true;
    error.value = null;
    const rate = assignRate.value ? { amount: assignRate.value, currency: 'USD' } : undefined;
    await assignPerson(orgId(), gig.value.id, offerPosition.value, offerPersonId.value, rate, offerCallIds.value);
    showOfferDialog.value = false;
    offerPosition.value = null;
    offerPersonId.value = null;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to assign';
  } finally {
    isOffering.value = false;
  }
}

// ---- A booking's own linked calls (subset of its position's) ----
const showBookingCallsDialog = ref(false);
const bookingCallsPosition = ref<Position | null>(null);
const bookingCallsBooking = ref<Booking | null>(null);
const bookingCallIds = ref<string[]>([]);
const savingBookingCalls = ref(false);
const bookingCallsOptions = computed<Call[]>(() =>
  bookingCallsPosition.value ? positionCalls(bookingCallsPosition.value) : [],
);

function openBookingCalls(pos: Position, booking: Booking) {
  bookingCallsPosition.value = pos;
  bookingCallsBooking.value = booking;
  bookingCallIds.value = [...booking.callIds];
  showBookingCallsDialog.value = true;
}

async function saveBookingCalls() {
  if (!gig.value || !bookingCallsPosition.value || !bookingCallsBooking.value) return;
  try {
    savingBookingCalls.value = true;
    error.value = null;
    await setBookingCallIds(orgId(), gig.value.id, bookingCallsPosition.value, bookingCallsBooking.value, bookingCallIds.value);
    showBookingCallsDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to update calls';
  } finally {
    savingBookingCalls.value = false;
  }
}

// ---- Edit a draft booking's rate ----
const showEditRateDialog = ref(false);
const bookingRatePosition = ref<Position | null>(null);
const bookingRateBooking = ref<Booking | null>(null);
const bookingRateAmount = ref(0);
const savingBookingRate = ref(false);

function openEditBookingRate(pos: Position, booking: Booking) {
  bookingRatePosition.value = pos;
  bookingRateBooking.value = booking;
  bookingRateAmount.value = booking.rate?.amount ?? pos.rate.amount;
  showEditRateDialog.value = true;
}

async function saveBookingRate() {
  if (!gig.value || !bookingRatePosition.value || !bookingRateBooking.value) return;
  try {
    savingBookingRate.value = true;
    error.value = null;
    const rate: RateEntry = { amount: bookingRateAmount.value, currency: bookingRatePosition.value.rate.currency };
    await updateBooking(orgId(), gig.value.id, bookingRatePosition.value.id, bookingRateBooking.value.id, { rate });
    showEditRateDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to update rate';
  } finally {
    savingBookingRate.value = false;
  }
}

// ---- Cancel a booking (pull an offer, or release someone who'd already
// committed) — transitions to CANCELLED rather than deleting, so the record
// (and any fee) stays visible on this position. See cancelBooking in
// @gigdriver/client for why this is always a single atomic write. ----
const showCancelBookingDialog = ref(false);
const cancelBookingPosition = ref<Position | null>(null);
const cancelBookingTarget = ref<Booking | null>(null);
const cancelReason = ref('');
const cancelFeeAmount = ref<number | null>(null);
const cancelFeeStatus = ref<CancellationFeeStatus>('WAIVED');
const cancellingId = ref<string | null>(null);

function openCancelBooking(pos: Position, booking: Booking) {
  cancelBookingPosition.value = pos;
  cancelBookingTarget.value = booking;
  cancelReason.value = '';
  cancelFeeAmount.value = null;
  cancelFeeStatus.value = 'WAIVED';
  showCancelBookingDialog.value = true;
}

async function submitCancelBooking() {
  if (!gig.value || !cancelBookingTarget.value || !authStore.currentUser) return;
  const booking = cancelBookingTarget.value;
  try {
    cancellingId.value = booking.id;
    error.value = null;
    const fee =
      (booking.state === 'ACCEPTED' || booking.state === 'BOOKED') && cancelFeeAmount.value
        ? { amount: cancelFeeAmount.value, currency: booking.rate?.currency ?? 'USD' }
        : undefined;
    await cancelBooking(orgId(), gig.value.id, booking, {
      cancelledBy: authStore.currentUser.uid,
      reason: cancelReason.value.trim() || undefined,
      fee,
      feeStatus: fee ? cancelFeeStatus.value : undefined,
    });
    showCancelBookingDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to cancel booking';
  } finally {
    cancellingId.value = null;
  }
}

// ---- Coordinator-facing person history (reachable from the cancel dialog
// above, for the fee/rebook judgment call, and from the People page) ----
const showPersonHistoryDialog = ref(false);
const personHistoryPersonId = ref('');
const personHistoryPersonName = ref('');

function openPersonHistoryFromCancel() {
  if (!cancelBookingTarget.value) return;
  personHistoryPersonId.value = cancelBookingTarget.value.personId;
  personHistoryPersonName.value = personDisplayName(cancelBookingTarget.value);
  showPersonHistoryDialog.value = true;
}

// Budget-fill (BuildPlan §9): a coordinator's own over-budget position is
// allowed anyway if the linked calls' combined budget — every position
// touching any of this booking's own callIds, summed across whichever
// budget group each belongs to — still covers the combined assigned total.
// Handles the common case (a Tech position over its own cap, offset by an
// Assistant position with room to spare on the same call); a booking with
// no linked calls yet has nothing to offset against.
function callBudgetTotals(callIds: string[]) {
  const positionIdsOnCalls = new Set(
    gigsStore.positions.filter(p => p.callIds.some(id => callIds.includes(id))).map(p => p.id),
  );
  const relevantGroups = budgetSummaries.value.filter(g => positionIdsOnCalls.has(g.positionId));
  return relevantGroups.reduce(
    (totals, g) => ({ budgeted: totals.budgeted + g.budgeted, assigned: totals.assigned + g.actualOfferedCost }),
    { budgeted: 0, assigned: 0 },
  );
}

const showOverrideDialog = ref(false);
const overrideReason = ref('');
const overriding = ref(false);
const overrideInfo = ref<BudgetPositionSummary | null>(null);
const overridePendingBooking = ref<Booking | null>(null);

// ---- Send Offer preview — shown before commitSendOffer actually runs ----
const showOfferPreviewDialog = ref(false);
const previewBooking = ref<Booking | null>(null);
const previewPosition = computed(() =>
  previewBooking.value ? gigsStore.positions.find(p => p.id === previewBooking.value!.positionId) : undefined,
);
const previewCalls = computed<Call[]>(() => {
  const booking = previewBooking.value;
  if (!booking) return [];
  const callById = new Map(calls.value.map(c => [c.id, c]));
  return booking.callIds.map(id => callById.get(id)).filter((c): c is Call => Boolean(c));
});
// The rate actually offered to this person (set at assign time) — falls
// back to the position's rate only if the booking somehow has none.
const previewBookingRate = computed(() => {
  const booking = previewBooking.value;
  const pos = previewPosition.value;
  return {
    rate: booking?.rate ?? pos?.rate ?? { amount: 0, currency: 'USD' },
    rateType: booking?.rateType ?? pos?.rateType,
  };
});
const previewOfferTotal = computed(() => {
  const booking = previewBooking.value;
  const pos = previewPosition.value;
  if (!booking || !pos) return 0;
  const day = sharedDayOf(booking);
  if (day && sharedCosts.value) {
    // The combined offer: one estimate for the whole day, across every gig.
    return day.members.reduce((sum, m) => sum + sharedTotal(sharedCosts.value!.estimate, m.bookingId), 0);
  }
  return costForBooking(booking, pos, calls.value, rateCardsStore.rateCards, costZone.value, orgStore.currentOrg?.holidays);
});
// The position's rate-card role terms (OT threshold, rest period, hourly
// minimum) — undefined for a manually-priced position, in which case the
// text below falls back to the same otStartHrs=10 default the cost math
// itself uses, matching what's actually in the offer email.
const previewRoleRate = computed(() => {
  const pos = previewPosition.value;
  if (!pos?.rateCardId || !pos.rateCardRole) return undefined;
  const card = rateCardsStore.rateCards.find(c => c.id === pos.rateCardId);
  return card?.roles[pos.rateCardRole];
});
function hourWord(n: number): string {
  return `${n} hour${n === 1 ? '' : 's'}`;
}
// Mirrors functions/src/email.ts's offerCreatedEmail termBullets branching —
// same two conditions (useStandardTimekeeping, then rate type), same
// defaults, so what a coordinator previews here matches what actually goes
// out in the offer email.
const previewTermsText = computed(() => {
  const roleRate = previewRoleRate.value;
  const useDayRate = previewBookingRate.value.rateType !== 'hourly';
  if (roleRate?.useStandardTimekeeping) {
    const otStartHrs = roleRate.otStartHrs ?? (useDayRate ? 10 : 8);
    const ot2StartHrs = roleRate.otSecondStartHrs ?? 12;
    const parts = [`Overtime: 1.5x after ${hourWord(otStartHrs)}, 2x after ${hourWord(ot2StartHrs)}`];
    const rest = roleRate.restPeriodHrs ?? 10;
    if (useDayRate) {
      // The estimate assumes the on-site coordinator provides breaks and meals;
      // rest and meal rules are applied to actual time worked.
      parts.push(`Day rate covers up to ${hourWord(otStartHrs)} per day — your estimate assumes the on-site coordinator provides your breaks and meals`);
      parts.push(`${hourWord(rest)} rest between days, applied to actual time worked: if it isn't given, the following day bills at 1.5x the day rate`);
      parts.push('Meal break required before hour 5, applied to actual time worked: the day otherwise concludes at hour 5 (can continue as OT after a delayed break)');
    } else {
      const firstCallMinHrs = roleRate.firstCallMinHrs ?? 4;
      const subsequentCallMinHrs = roleRate.subsequentCallMinHrs ?? 2;
      parts.push(`${hourWord(firstCallMinHrs)} minimum for your first call of the day, ${hourWord(subsequentCallMinHrs)} for each additional call`);
      parts.push(`${hourWord(rest)} rest between calls to reset the overtime clock`);
      parts.push('Meal break required after 5 hours');
    }
    parts.push('Recognized holidays bill as overtime for the full call/day');
    return parts.join(' · ');
  }
  const otStartHrs = roleRate?.otStartHrs ?? 10;
  const parts = [`Overtime after ${hourWord(otStartHrs)}`];
  const rest = roleRate?.restPeriodHrs;
  if (rest) parts.push(`${hourWord(rest)} rest period between daily calls`);
  const min = roleRate?.minHourlyBill;
  if (min) parts.push(`${hourWord(min)} minimum per call`);
  return parts.join(' · ');
});

function openOfferPreview(booking: Booking) {
  previewBooking.value = booking;
  showOfferPreviewDialog.value = true;
}

async function confirmSendOffer() {
  const booking = previewBooking.value;
  if (!booking) return;
  showOfferPreviewDialog.value = false;
  previewBooking.value = null;
  await sendOfferAction(booking);
}

// ---- shared-day actions ----
const gigNames = ref<Record<string, string>>({});
const showLinkDialog = ref(false);
const linkTarget = ref<{ booking: Booking; position: Position } | null>(null);
function openLinkSharedDay(pos: Position, booking: Booking) {
  linkTarget.value = { booking, position: pos };
  showLinkDialog.value = true;
}
function onSharedDayLinked() {
  // The org-wide watcher picks up the new link and reloads the other gigs.
  error.value = null;
}
// Every member must be loaded to send: the combined offer is all-or-nothing.
function sharedMembersFor(day: SharedDay): Booking[] | null {
  const members = day.members.map(m => sharedMemberData.value.get(m.bookingId)?.booking);
  return members.every(Boolean) ? (members as Booking[]) : null;
}
async function sendSharedDayOffer(day: SharedDay, confirmFirst = true) {
  const members = sharedMembersFor(day);
  if (!members) {
    error.value = 'The linked gig is still loading — try again in a moment.';
    return;
  }
  // The same budget guard as a single offer, for this gig's own positions.
  const over = members.filter(b => b.gigId === gig.value?.id).map(b => budgetGroupForPosition(b.positionId)).find(g => g?.overBudget);
  if (over && !orgStore.isManagerPlus) {
    error.value = `${over.role} is over its $${over.budgeted.toFixed(2)} budget — ask an owner/admin/manager to send this shared offer.`;
    return;
  }
  const total = day.members.reduce((sum, m) => sum + sharedTotal(sharedCosts.value!.estimate, m.bookingId), 0);
  if (confirmFirst && !confirm(`Send ${day.personName ?? 'this person'} one offer covering ${members.length} gigs, about $${total.toLocaleString()} for the day?`)) return;
  error.value = null;
  try {
    await sharedStore.sendOffer(members);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to send the shared offer';
  }
}
async function unlinkSharedDayAction(day: SharedDay) {
  if (!confirm('Unlink this shared day? Each booking goes back to its own offer and day rate.')) return;
  error.value = null;
  try {
    await sharedStore.unlink(orgId(), day.id);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to unlink';
  }
}

async function commitSendOffer(booking: Booking) {
  const day = sharedDayOf(booking);
  if (day) return sendSharedDayOffer(day, false);
  sendingOfferId.value = booking.id;
  error.value = null;
  try {
    await sendOffer(gig.value!.id, booking);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to send offer';
  } finally {
    sendingOfferId.value = null;
  }
}

async function sendOfferAction(booking: Booking) {
  if (!gig.value) return;
  const group = budgetGroupForPosition(booking.positionId);
  if (!group?.overBudget) {
    await commitSendOffer(booking);
    return;
  }

  const role = orgStore.myRole;
  if (role === 'owner' || role === 'admin' || role === 'manager') {
    overrideInfo.value = group;
    overridePendingBooking.value = booking;
    overrideReason.value = '';
    showOverrideDialog.value = true;
    return;
  }

  if (role === 'coordinator') {
    if (booking.callIds.length === 0) {
      error.value = `${group.role} is over its $${group.budgeted.toFixed(2)} budget — link calls to check for same-call budget room, or ask an owner/admin/manager to override.`;
      return;
    }
    const callTotals = callBudgetTotals(booking.callIds);
    if (callTotals.budgeted > 0 && callTotals.assigned <= callTotals.budgeted) {
      await commitSendOffer(booking);
      await logActivity(
        orgId(),
        'position',
        booking.positionId,
        'budget_fill',
        authStore.currentUser?.uid ?? 'unknown',
        { role: group.role, positionBudgeted: group.budgeted, positionAssigned: group.actualOfferedCost, callBudgeted: callTotals.budgeted, callAssigned: callTotals.assigned },
      );
      return;
    }
    error.value = `${group.role} is over its $${group.budgeted.toFixed(2)} budget, and the linked calls' combined budget doesn't cover it either — ask an owner/admin/manager to override.`;
    return;
  }

  error.value = `${group.role} is over its $${group.budgeted.toFixed(2)} budget.`;
}

async function confirmOverride() {
  const booking = overridePendingBooking.value;
  const group = overrideInfo.value;
  if (!booking || !group || !overrideReason.value.trim()) return;
  try {
    overriding.value = true;
    await commitSendOffer(booking);
    await logActivity(
      orgId(),
      'position',
      booking.positionId,
      'budget_override',
      authStore.currentUser?.uid ?? 'unknown',
      { role: group.role, budgeted: group.budgeted, assigned: group.actualOfferedCost, reason: overrideReason.value.trim() },
    );
    showOverrideDialog.value = false;
    overridePendingBooking.value = null;
    overrideInfo.value = null;
  } finally {
    overriding.value = false;
  }
}

async function unassignAction(booking: Booking) {
  if (!gig.value || !confirm('Remove this draft assignment?')) return;
  try {
    unassigningId.value = booking.id;
    error.value = null;
    await unassignPerson(gig.value.id, booking);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to remove assignment';
  } finally {
    unassigningId.value = null;
  }
}

async function respondBooking(booking: Booking, accept: boolean) {
  try {
    respondingId.value = booking.id;
    error.value = null;
    const day = sharedDayOf(booking);
    if (day) {
      // One answer for the whole day, in one atomic batch.
      const members = sharedMembersFor(day);
      if (!members) throw new Error('The linked gig is still loading — try again in a moment.');
      await sharedStore.respond(members, accept);
      return;
    }
    await respondToOffer(orgId(), booking.gigId, booking.positionId, booking.id, accept);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to respond';
  } finally {
    respondingId.value = null;
  }
}
</script>
